import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import AppTextField from "../components/AppTextField";
import AppSelectionField from "../components/AppSelectionField";
import AppFullscreenDialog from "../components/AppFullscreenDialog";
import DocumentComparisonViewport from "../components/DocumentComparisonViewport";
import AppDialogSurface from "../components/AppDialogSurface";
import LazyAreaBoundary from "../components/LazyAreaBoundary";
import AppCard from "../components/AppCard";
import WorkflowPageHeader from "../components/WorkflowPageHeader";
import { APP_UI } from "../ui/appUi";
import MaterialMasterMismatchDialog from "../components/MaterialMasterMismatchDialog";
import MaterialMasterSuggestionsDialog from "../components/MaterialMasterSuggestionsDialog";
import ScanDetailsDialog from "../components/ScanDetailsDialog";
import StartPage, { type StartSection } from "../components/StartPage";
import PersonSelectionSheet, { type SelectableUserName } from "../components/PersonSelectionSheet";
import AllocationSelectionSheet from "../components/AllocationSelectionSheet";
import type { WithdrawalVersionData } from "../types/withdrawalHistory";
import { fetchHistoryDetail } from "../services/withdrawalHistoryService";
import { findMaterialByNumber, findMaterialSuggestions } from "../services/materialMasterDataService";
import { searchAllocationMasterData } from "../services/allocationMasterDataService";
import type { AllocationMasterRecord } from "../types/allocationMasterData";
import { useAppStatus } from "../status/useAppStatus";
import { formatAllocationNumber } from "../utils/allocationNumber";
import { requestBrowserBack, returnToAppRoot, useBackLayer } from "../hooks/useBackLayer";
import { getLastKnownAuthUser } from "../services/authService";
import { recordVisualLabQualityEvent } from "../services/visualLabService";
import { APP_VERSION } from "../appVersion";
import { getLastScanFailureFlight } from "../services/scanFailureFlightRecorderService";
import {
  createEmptyMaterialMasterComparison,
  type MaterialMasterComparison,
  type MaterialMasterMismatchItem,
  type MaterialMismatchField,
  type MaterialMasterSuggestion,
} from "../types/materialMasterData";
import {
  createEmptyOcrDetails,
  createEmptyRecognizedLabelData,
  type RecognizedLabelData,
  type ScanCaptureDetails,
  type ScanOcrDetails,
  type ScanOcrParallelProductionEvidence,
} from "../types/scanWorkflow";
import { isValidStorageAddress, normalizeStorageAddress, parseStorageAddress } from "../utils/storageAddress";
import type { PrecomputedFinalOcrPayload } from "../components/CameraCaptureDialog";
import type { WithdrawalDocumentArtifacts, WithdrawalDocumentData } from "../services/withdrawalDocumentService";
import { saveDraftOfflineFirst, syncLocalDraft, updateDraftDiagnostics, updateDraftDocumentArtifacts } from "../services/draftService";
import { nextLocalWithdrawalNumber } from "../services/withdrawalIdentityService";
import { createWithdrawal, createWithdrawalWithIdentity, queueWithdrawalEdit, type WithdrawalPersistenceTelemetryPhase } from "../services/withdrawalService";
import { createWithdrawalCompletionTelemetryBase, saveWithdrawalCompletionTelemetry } from "../services/withdrawalCompletionTelemetryService";
import { compareAndSwapLocalWithdrawal, getLocalWithdrawal, saveRecoverySnapshot } from "../storage/offlineDatabase";
import { syncSingleWithdrawal } from "../services/syncService";
import { fetchUserNames, getCachedUserNames } from "../services/settingsService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fetchWithdrawalEmailStatus, type EmailJobStatus } from "../api/emailStatus";
import { uploadWithdrawalFileRaw } from "../api/sync";
import { buildScanDebugImage } from "../services/scanDebugImageService";
import {
  loadAllocationMasterPage,
  loadAdminPage,
  loadCameraCaptureDialog,
  loadHistoryPage,
  loadMaterialMasterPage,
  loadSettingsPage,
  loadVisualLabPage,
  loadVisionOptimizerPage,
  pendingLazyStartSection,
  rememberPendingLazyStartSection,
  clearPendingLazyStartSection,
  type LazyStartSection,
} from "../services/lazyPageLoaders";
import { trackRuntimePromise } from "../services/runtimeActivityService";
import { createVisionCaptureCase, type VisionCaptureResponse } from "../services/visionCaptureService";
import { fetchVisionBootstrapCapturePolicy, type VisionBootstrapCapturePolicy } from "../services/visionOptimizerService";
import { completeRecognitionRun } from "../services/recognitionLatencyService";
import { productionExecutionProfileEvidence, recognitionParallelismEvidence } from "../services/recognitionParallelismProbeService";
import { fullFidelityPerformanceEvidence } from "../services/recognitionPerformanceContract";
import { getParallelRuntimeSoakSnapshot } from "../services/parallelRuntimeSoakService";
import { scheduleRecognitionParallelShadow } from "../services/recognitionParallelShadowService";
import { scheduleOpenCvParallelShadow } from "../services/openCvParallelShadowService";
import { finalizeStep2Uncertainty, resetStep2Uncertainty } from "../services/step2UncertaintyUxService";
import {
  buildDraftVisionFeedbackBlob,
  buildVisionFeedbackBlob,
  createRetakeSignal,
  createVisionFeedbackBaseline,
  readVisionFeedbackBaseline,
  type VisionFeedbackBaseline,
  type VisionFeedbackField,
  type VisionFeedbackIntervention,
  type VisionRetakeSignal,
} from "../services/visionFeedbackService";

// Große Fachbereiche werden erst geladen, wenn der Benutzer sie tatsächlich öffnet.
// Das hält Startseite/Login frei von VO, Visual Lab, Admin, Historie und Kamera-
// Runtime und reduziert Parse-/Evaluate-Arbeit beim ersten PWA-Start deutlich.
const CameraCaptureDialog = lazy(loadCameraCaptureDialog);
const MaterialMasterPage = lazy(loadMaterialMasterPage);
const AllocationMasterPage = lazy(loadAllocationMasterPage);
const HistoryPage = lazy(loadHistoryPage);
const SettingsPage = lazy(loadSettingsPage);
const AdminPage = lazy(loadAdminPage);
const VisualLabPage = lazy(loadVisualLabPage);
const VisionOptimizerPage = lazy(loadVisionOptimizerPage);

let documentServicePromise: Promise<typeof import("../services/withdrawalDocumentService")> | null = null;
function createWithdrawalDocumentArtifacts(data: WithdrawalDocumentData): Promise<WithdrawalDocumentArtifacts> {
  documentServicePromise ??= trackRuntimePromise(
    "lazy:document-runtime",
    "PDF-/Dokument-Runtime",
    "lazy",
    () => import("../services/withdrawalDocumentService"),
  );
  return documentServicePromise.then((service) => service.createWithdrawalDocumentArtifacts(data));
}

type CompletionBlobSources = {
  pdfBlob?: Blob | null;
  pdfPreviewBlob?: Blob | null;
  labelBlob?: Blob | null;
  sourceBlob?: Blob | null;
  detectionSourceBlob?: Blob | null;
  analysisInputBlob?: Blob | null;
  rectificationInputBlob?: Blob | null;
  ocrWarpBlob?: Blob | null;
  visionFeedbackBlob?: Blob | null;
  liveTrainingBlob?: Blob | null;
};

function completionBlobBytes(input: CompletionBlobSources) {
  const result = {
    pdf: input.pdfBlob?.size ?? 0,
    webp: input.pdfPreviewBlob?.size ?? 0,
    label: input.labelBlob?.size ?? 0,
    source: input.sourceBlob?.size ?? 0,
    detection_source: input.detectionSourceBlob?.size ?? 0,
    analysis_input: input.analysisInputBlob?.size ?? 0,
    rectification_input: input.rectificationInputBlob?.size ?? 0,
    ocr_warp: input.ocrWarpBlob?.size ?? 0,
    vision_feedback: input.visionFeedbackBlob?.size ?? 0,
    live_training: input.liveTrainingBlob?.size ?? 0,
    total: 0,
  };
  result.total = result.pdf + result.webp + result.label + result.source + result.detection_source
    + result.analysis_input + result.rectification_input + result.ocr_warp + result.vision_feedback + result.live_training;
  return result;
}

let labelOcrServicePromise: Promise<typeof import("../services/labelOcrService")> | null = null;
type RecognizeLabelDataArgs = Parameters<typeof import("../services/labelOcrService").recognizeLabelData>;
async function recognizeLabelData(...args: RecognizeLabelDataArgs) {
  labelOcrServicePromise ??= trackRuntimePromise(
    "lazy:ocr-runtime",
    "OCR-Runtime",
    "lazy",
    () => import("../services/labelOcrService"),
  );
  const service = await labelOcrServicePromise;
  return service.recognizeLabelData(...args);
}

// PV-9_OCR_SERVICE_HELPER_BEGIN
async function getLabelOcrService() {
  labelOcrServicePromise ??= trackRuntimePromise(
    "lazy:ocr-runtime",
    "OCR-Runtime",
    "lazy",
    () => import("../services/labelOcrService"),
  );
  return labelOcrServicePromise;
}
// PV-9_OCR_SERVICE_HELPER_END

function LazyAreaFallback({ label = "Bereich wird geladen …" }: { label?: string }) {
  return (
    <Stack spacing={1} sx={{ minHeight: "70dvh", alignItems: "center", justifyContent: "center", color: APP_UI.textMuted, bgcolor: "transparent" }}>
      <CircularProgress size={24} sx={{ color: APP_UI.accent }} />
      <Typography variant="caption">{label}</Typography>
    </Stack>
  );
}


function normalizeComparisonText(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/[‐‑‒–—−]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleUpperCase("de-DE");
}

function buildMismatches(
  labelData: RecognizedLabelData,
  masterDescription: string,
  masterType: string,
  masterStorageLocations: string[]
): MaterialMasterMismatchItem[] {
  const mismatches: MaterialMasterMismatchItem[] = [];
  const description = labelData.materialText.trim();

  if (
    normalizeComparisonText(description) !== normalizeComparisonText(masterDescription)
  ) {
    mismatches.push({
      field: "materialText",
      label: "Bezeichnung",
      labelValue: description,
      masterValue: masterDescription,
    });
  }

  const type = labelData.materialType.trim();
  if (normalizeComparisonText(type) !== normalizeComparisonText(masterType)) {
    mismatches.push({
      field: "materialType",
      label: "Typ",
      labelValue: type,
      masterValue: masterType,
    });
  }

  const storageAddress = normalizeStorageAddress(labelData.storageAddress);
  if (masterStorageLocations.length > 0 && !masterStorageLocations.includes(storageAddress)) {
    mismatches.push({
      field: "storageAddress",
      label: "Lagerort",
      labelValue: storageAddress,
      masterValue: masterStorageLocations[0],
    });
  }

  return mismatches;
}

function appendOrReplaceProcessStep(
  details: ScanCaptureDetails,
  id: string,
  label: string,
  status: "running" | "done" | "error" | "skipped",
  detail: string | null,
  durationMs: number | null = null
): ScanCaptureDetails {
  const existingIndex = details.processingSteps.findIndex((step) => step.id === id);
  const previous = existingIndex >= 0 ? details.processingSteps[existingIndex] : null;
  const nextStep = {
    id,
    label,
    status,
    detail,
    durationMs: durationMs ?? previous?.durationMs ?? null,
  };

  return {
    ...details,
    processingSteps:
      existingIndex >= 0
        ? details.processingSteps.map((step, index) => index === existingIndex ? nextStep : step)
        : [...details.processingSteps, nextStep],
  };
}


interface ScanWorkflowPageProps {
  onStartPageChange?: (isStartPage: boolean) => void;
}

function continuedVersionSignature(version: WithdrawalVersionData, storageAddress: string): string {
  const accountingDescription = version.accounting_number
    ? `${version.accounting_system ?? ""} - ${version.accounting_subarea ?? ""}`
    : "";
  return JSON.stringify({
    withdrawalDate: version.withdrawal_date,
    materialNumber: version.material_number.trim(),
    materialText: version.material_text.trim(),
    materialType: (version.material_type ?? "").trim(),
    storageAddress: storageAddress.trim(),
    quantityRemoved: (version.quantity_removed ?? "").trim(),
    currentStock: (version.current_stock ?? "").trim(),
    persons: version.persons.map((person) => person.name),
    accountingNumber: version.accounting_number ?? "",
    accountingDescription,
    orderInfo: (version.order_info ?? "").trim(),
    allocationPlant: version.accounting_system ?? null,
    allocationArea: version.accounting_subarea ?? null,
    allocationManufacturer: version.accounting_manufacturer ?? null,
  });
}

export default function ScanWorkflowPage({ onStartPageChange }: ScanWorkflowPageProps) {
  const { services, setActivity, reportIssue, clearIssue, setServiceStatus } = useAppStatus();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [startSectionTrail, setStartSectionTrail] = useState<Array<StartSection | null>>(() => { const pending = pendingLazyStartSection() as StartSection | null; return pending ? [null, pending] : [null]; });
  const startSection = startSectionTrail[startSectionTrail.length - 1] ?? null;
  const [visionOptimizerTarget, setVisionOptimizerTarget] = useState<{ section?: string; jobId?: string; candidateId?: string; decisionKey?: string } | null>(null);
  const setStartSection = useCallback((next: StartSection | null) => {
    // Ziel synchron VOR dem React-Render persistieren. Ein Vite-/SW-Full-Reload
    // kann bereits während des ersten Lazy-Imports ausgelöst werden; ein Effect
    // nach dem Render wäre dafür im schlechtesten Fall zu spät.
    if (next) rememberPendingLazyStartSection(next as LazyStartSection);
    else clearPendingLazyStartSection();
    setStartSectionTrail((current) => {
      if (next === null) return [null];
      return current[current.length - 1] === next ? current : [...current, next];
    });
  }, []);
  const [historyTargetId, setHistoryTargetId] = useState<number | null>(null);
  const [historyTargetLocalUuid, setHistoryTargetLocalUuid] = useState<string | null>(null);
  const [openAdminFailureSimulation, setOpenAdminFailureSimulation] = useState(false);
  const [openSystemNotificationsFromNotice, setOpenSystemNotificationsFromNotice] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<MaterialMasterSuggestion[]>([]);
  const [suggestionTotal, setSuggestionTotal] = useState(0);
  const [suggestionPage, setSuggestionPage] = useState(0);
  const fuzzyPageSize = 10;
  const [draftSaving, setDraftSaving] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [withdrawalDate, setWithdrawalDate] = useState("");
  const [quantityRemoved, setQuantityRemoved] = useState("");
  const [currentStock, setCurrentStock] = useState("");
  const [selectedPersons, setSelectedPersons] = useState<string[]>([]);
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [availableNames, setAvailableNames] = useState<SelectableUserName[]>([]);
  const [pendingNewNames, setPendingNewNames] = useState<string[]>([]);
  const [step2ValidationAttempted, setStep2ValidationAttempted] = useState(false);
  const [step3ValidationAttempted, setStep3ValidationAttempted] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [allocationQuery, setAllocationQuery] = useState("");
  const [allocationResults, setAllocationResults] = useState<AllocationMasterRecord[]>([]);
  const [allocationTotal, setAllocationTotal] = useState(0);
  const [allocationPage, setAllocationPage] = useState(0);
  const allocationPageSize = 50;
  const [selectedAllocation, setSelectedAllocation] = useState<AllocationMasterRecord | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [completedId, setCompletedId] = useState<number | null>(null);
  const [voSimulation, setVoSimulation] = useState(false);
  const [manualWithdrawal, setManualWithdrawal] = useState(false);
  const [voCaptureClientUuid, setVoCaptureClientUuid] = useState<string | null>(null);
  const [completedVoCapture, setCompletedVoCapture] = useState<VisionCaptureResponse | null>(null);
  const [voBootstrapPolicy, setVoBootstrapPolicy] = useState<VisionBootstrapCapturePolicy | null>(null);
  const [completedWithdrawalNumber, setCompletedWithdrawalNumber] = useState<number | null>(null);
  const [completionDocumentsComplete, setCompletionDocumentsComplete] = useState<boolean | null>(null);
  const [completionSynced, setCompletionSynced] = useState<boolean | null>(null);
  const [completionEmailStatus, setCompletionEmailStatus] = useState<EmailJobStatus | null>(null);
  const [completedClientUuid, setCompletedClientUuid] = useState<string | null>(null);
  const [completionGraceActive, setCompletionGraceActive] = useState(false);
  const [completionEmailError, setCompletionEmailError] = useState<string | null>(null);
  const [editWithdrawalId, setEditWithdrawalId] = useState<number | null>(null);
  const [editClientUuid, setEditClientUuid] = useState<string | null>(null);
  const [editBaseVersion, setEditBaseVersion] = useState<number | null>(null);
  const [editWithdrawalNumber, setEditWithdrawalNumber] = useState<number | null>(null);
  const [draftClientUuid, setDraftClientUuid] = useState<string | null>(null);
  const [draftWithdrawalNumber, setDraftWithdrawalNumber] = useState<number | null>(null);
  const [controlFile, setControlFile] = useState<File | null>(null);
  const [analysisFile, setAnalysisFile] = useState<File | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [analysisInputFile, setAnalysisInputFile] = useState<File | null>(null);
  const [rectificationInputFile, setRectificationInputFile] = useState<File | null>(null);
  const [liveTrainingFile, setLiveTrainingFile] = useState<File | null>(null);
  const [originalCaptureFile, setOriginalCaptureFile] = useState<File | null>(null);
  const [scanDetails, setScanDetails] = useState<ScanCaptureDetails | null>(null);
  const [continuedWorkflow, setContinuedWorkflow] = useState<"draft" | "edit" | null>(null);
  const [labelData, setLabelData] = useState<RecognizedLabelData>(
    createEmptyRecognizedLabelData
  );
  const [masterComparison, setMasterComparison] = useState<MaterialMasterComparison>(
    createEmptyMaterialMasterComparison
  );
  const [rememberedMismatch, setRememberedMismatch] = useState<MaterialMasterComparison | null>(null);
  const [ocrDetails, setOcrDetails] = useState<ScanOcrDetails>(createEmptyOcrDetails);
  const [notice, setNotice] = useState<string | null>(null);
  const [abortDialogOpen, setAbortDialogOpen] = useState(false);
  const [abortTransition, setAbortTransition] = useState(false);
  const [controlPreviewUrl, setControlPreviewUrl] = useState<string | null>(null);
  const ocrGenerationRef = useRef(0);
  const autoMismatchEligibleRef = useRef(false);
  const lastSavedDraftSignatureRef = useRef<string | null>(null);
  const [lastSavedDraftSignature, setLastSavedDraftSignature] = useState<string | null>(null);
  const continuedBaselineSignatureRef = useRef<string | null>(null);
  const preparedDocumentRef = useRef<{ signature: string; promise: ReturnType<typeof createWithdrawalDocumentArtifacts>; startedAtMs: number } | null>(null);
  const reportedUnknownMaterialsRef = useRef<Set<string>>(new Set());
  const visionFeedbackBaselineRef = useRef<VisionFeedbackBaseline | null>(null);
  const visionFeedbackInterventionsRef = useRef<Partial<Record<VisionFeedbackField, VisionFeedbackIntervention>>>({});
  const visionRetakesRef = useRef<VisionRetakeSignal[]>([]);
  const step2ConfirmedAtRef = useRef<string | null>(null);

  useEffect(() => {
    if (!completedClientUuid) return;
    let active = true;
    let pollTimer: number | null = null;
    const graceTimer = window.setTimeout(() => { if (active) setCompletionGraceActive(false); }, 1500);

    const refreshLocalCompletion = async () => {
      const local = await getLocalWithdrawal(completedClientUuid).catch(() => null);
      if (!active || !local) return;
      if (local.server_withdrawal_id) setCompletedId(local.server_withdrawal_id);
      setCompletedWithdrawalNumber(local.server_withdrawal_number ?? local.local_withdrawal_number ?? null);
      const synced = local.sync_status === "synced";
      setCompletionSynced(synced);
      setCompletionDocumentsComplete(synced);
      if (synced) {
        setCompletionGraceActive(false);
        return;
      }
      pollTimer = window.setTimeout(() => { void refreshLocalCompletion(); }, 500);
    };

    const onRefresh = () => { void refreshLocalCompletion(); };
    window.addEventListener("materialentnahme:status-refresh", onRefresh);
    void refreshLocalCompletion();
    return () => {
      active = false;
      window.clearTimeout(graceTimer);
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      window.removeEventListener("materialentnahme:status-refresh", onRefresh);
    };
  }, [completedClientUuid]);

  useEffect(() => {
    if (completedId === null || completedId <= 0 || completionSynced !== true) return;

    let active = true;
    let timer: number | null = null;

    const poll = async () => {
      try {
        const job = await fetchWithdrawalEmailStatus(completedId);
        if (!active) return;
        setCompletionEmailStatus(job.status);
        setCompletionEmailError(job.last_error_user);
        if (job.status === "sent" || job.status === "failed") {
          // Die Abschlussansicht kennt den endgültigen Mailstatus deutlich früher
          // als der reguläre globale Status-Poll. Dynamic Island und
          // „Versand & Verlauf“ deshalb sofort invalidieren statt bis zum
          // nächsten Poll-Zyklus einen veralteten Wartestatus zu zeigen.
          window.dispatchEvent(new Event("materialentnahme:status-refresh"));
          return;
        }
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : "Versandstatus konnte nicht geprüft werden.";
        // Direkt nach dem Sync kann der Worker-Auftrag wenige Millisekunden später entstehen.
        // In diesem Fall weiter pollen, statt die Abschlussansicht mit einem technischen Fehler zu belasten.
        if (!message.includes("404")) setCompletionEmailError(message);
      }
      if (active) timer = window.setTimeout(() => { void poll(); }, 1000);
    };

    void poll();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [completedId, completionSynced]);

  const detailsAvailable = scanDetails !== null;

  const refreshVoBootstrapPolicy = useCallback(async () => {
    if (getLastKnownAuthUser()?.role !== "admin") return;
    try {
      const policy = await fetchVisionBootstrapCapturePolicy();
      setVoBootstrapPolicy(policy);
      if (!policy.available) {
        setVoSimulation(false);
        setVoCaptureClientUuid(null);
      }
    } catch {
      // VO-Testaufnahmen benötigen ohnehin eine Serververbindung. Ein fehlender
      // Policy-Abruf darf den normalen Entnahme-Workflow niemals beeinflussen.
    }
  }, []);

  useEffect(() => {
    if (getLastKnownAuthUser()?.role !== "admin" || step !== 1 || startSection !== null) return;
    const timer = window.setTimeout(() => { void refreshVoBootstrapPolicy(); }, 0);
    return () => window.clearTimeout(timer);
  }, [step, startSection, refreshVoBootstrapPolicy]);

  const openStartSection = (section: StartSection) => {
    if (section === "visionOptimizer") setVisionOptimizerTarget(null);
    // Ein History-Deep-Link aus „Versand & Verlauf“ ist ein einmaliges Ziel.
    // Beim normalen Öffnen über die Startseite darf niemals eine alte Auswahl
    // weiterverwendet werden.
    if (section === "history") {
      setHistoryTargetId(null);
      setHistoryTargetLocalUuid(null);
    }
    setStartSection(section);
  };

  useEffect(() => {
    const openHistoryEntry = (event: Event) => {
      const detail = (event as CustomEvent<{ withdrawalId?: number; clientUuid?: string }>).detail;
      if (!detail?.withdrawalId && !detail?.clientUuid) return;
      setHistoryTargetId(detail.withdrawalId ?? null);
      setHistoryTargetLocalUuid(detail.clientUuid ?? null);
      setStartSection("history");
      setStep(1);
      setCameraOpen(false);
    };
    window.addEventListener("materialentnahme:open-history-entry", openHistoryEntry);
    return () => window.removeEventListener("materialentnahme:open-history-entry", openHistoryEntry);
  }, [setStartSection]);

  useEffect(() => {
    const openHistory = () => {
      setHistoryTargetId(null);
      setHistoryTargetLocalUuid(null);
      setOpenAdminFailureSimulation(false);
      setStartSection("history");
      setStep(1);
      setCameraOpen(false);
    };
    const openFailureSimulation = () => {
      setOpenAdminFailureSimulation(true);
      setStartSection("admin");
      setStep(1);
      setCameraOpen(false);
    };
    const openPushSettings = () => {
      setOpenSystemNotificationsFromNotice(true);
      setStartSection("settings");
      setStep(1);
      setCameraOpen(false);
    };
    const openAppTarget = (event: Event) => {
      const targetUrl = (event as CustomEvent<{ targetUrl?: string }>).detail?.targetUrl;
      if (!targetUrl) return;
      let parsed: URL;
      try { parsed = new URL(targetUrl, window.location.origin); } catch { return; }
      if (parsed.pathname !== "/vision-optimizer") return;
      setVisionOptimizerTarget({
        section: parsed.searchParams.get("voSection") ?? "overview",
        jobId: parsed.searchParams.get("jobId") ?? undefined,
        candidateId: parsed.searchParams.get("candidateId") ?? undefined,
        decisionKey: parsed.searchParams.get("decisionKey") ?? undefined,
      });
      setStartSection("visionOptimizer");
      setStep(1);
      setCameraOpen(false);
    };
    window.addEventListener("materialentnahme:navigate-history", openHistory);
    window.addEventListener("materialentnahme:navigate-failure-simulation", openFailureSimulation);
    window.addEventListener("materialentnahme:open-push-settings", openPushSettings);
    window.addEventListener("materialentnahme:navigate-target", openAppTarget);
    return () => {
      window.removeEventListener("materialentnahme:navigate-history", openHistory);
      window.removeEventListener("materialentnahme:navigate-failure-simulation", openFailureSimulation);
      window.removeEventListener("materialentnahme:open-push-settings", openPushSettings);
      window.removeEventListener("materialentnahme:navigate-target", openAppTarget);
    };
  }, [setStartSection]);

  useEffect(() => () => {
    if (documentPreviewUrl) URL.revokeObjectURL(documentPreviewUrl);
  }, [documentPreviewUrl]);

  useEffect(() => {
    onStartPageChange?.(step === 1 && startSection === null && !cameraOpen);
  }, [step, startSection, cameraOpen, onStartPageChange]);

  useEffect(() => {
    // Zielbereich bereits beim Navigationsklick merken. Falls Vite-Dev, ein
    // Service-Worker-Wechsel oder ein Chunk-Reload die Seite während des ersten
    // Lazy-Imports neu lädt, wird derselbe Bereich danach wieder geöffnet statt
    // kommentarlos auf die Startseite zurückzufallen. Nach erfolgreichem Import
    // löscht der Loader diesen Marker wieder.
    if (step !== 1) return;
    if (startSection) rememberPendingLazyStartSection(startSection as LazyStartSection);
    else clearPendingLazyStartSection();
  }, [step, startSection]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [step, startSection]);

  const { requestClose: closeStartSectionByHistory } = useBackLayer({
    open: startSectionTrail.length > 1,
    layerKey: `main-page-${startSectionTrail.length - 1}`,
    kind: "page",
    onBack: () => setStartSectionTrail((current) => current.length > 1 ? current.slice(0, -1) : current),
  });

  useBackLayer({
    open: cameraOpen,
    layerKey: "workflow-camera",
    kind: "workflow",
    // X/Backdrop und Browser-/Maus-/Android-Zurück müssen denselben fachlichen
    // Abbruchpfad verwenden. Ist bereits etwas eingegeben, erscheint deshalb
    // überall dieselbe Entwurf/Verwerfen-Abfrage.
    onBack: requestAbortWithdrawal,
    rearmAfterBack: true,
  });

  useBackLayer({
    open: step > 1,
    layerKey: "workflow-step-stack",
    kind: "workflow",
    onBack: () => {
      if (continuedWorkflow !== null && (step === 2 || step === 3)) {
        requestAbortWithdrawal();
        return;
      }
      if (step === 2) {
        setManualWithdrawal(false);
        setStep(1);
        setCameraOpen(true);
        return;
      }
      setStep((step - 1) as 1 | 2 | 3 | 4);
    },
    cleanupOnProgrammaticClose: true,
    // Bei Schritt 2 endet ein normales Back im Kamera-/Startzustand; dort wird
    // kein alter Workflow-Sentinel neu angelegt. Ab Schritt 3 bleibt dagegen
    // eine weitere Workflow-Ebene offen. Bei fortgesetztem Schritt 2 bleibt die
    // Ebene ebenfalls bestehen, weil zunächst nur die Abbruchabfrage geöffnet wird.
    rearmAfterBack: step > 2 || (continuedWorkflow !== null && step === 2),
  });

  useEffect(() => {
    if (step !== 2) return;

    const materialNumber = labelData.materialNumber.trim();
    const validMaterialNumber = /^983\d{7}$/.test(materialNumber);
    const hasFuzzyInput = Boolean(
      materialNumber
      || labelData.materialText.trim()
      || labelData.materialType.trim()
      || labelData.storageAddress.trim()
    );
    let cancelled = false;

    async function loadSuggestions(): Promise<void> {
      if (!hasFuzzyInput) {
        setSuggestions([]);
        setSuggestionTotal(0);
        setSuggestionPage(0);
        setServiceStatus("masterData", "ok", "Keine Matching-Daten eingegeben");
        return;
      }

      setServiceStatus("masterData", "checking", "Ähnliche Materialien werden gesucht");
      try {
        const result = await findMaterialSuggestions(
          labelData.materialNumber,
          labelData.materialText,
          labelData.materialType,
          labelData.storageAddress,
          fuzzyPageSize,
          0
        );
        if (cancelled) return;
        setSuggestions(result.suggestions);
        setSuggestionTotal(result.total);
        setSuggestionPage(0);
        setServiceStatus(
          "masterData",
          "ok",
          result.total > 0
            ? `${result.total} Vorschlag/Vorschläge`
            : "Keine passenden Vorschläge"
        );
        if (autoMismatchEligibleRef.current && result.total > 0) {
          setSuggestionsOpen(true);
          autoMismatchEligibleRef.current = false;
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          error instanceof Error
            ? error.message
            : "Materialstammliste konnte nicht geprüft werden.";
        setServiceStatus("masterData", "error", "Nicht verfügbar");
        reportIssue({
          id: "master-error",
          severity: "error",
          title: "Materialstammliste nicht verfügbar",
          detail: message,
        });
      }
    }

    async function runComparison(): Promise<void> {
      const comparisonStartedAt = performance.now();

      // Solange die Materialnummer nicht der Regel entspricht, gibt es
      // keinen Materialstamm-Abgleich. Stattdessen kann das vorhandene
      // Materialvorschläge mit den momentan verfügbaren Feldern genutzt werden.
      if (!validMaterialNumber) {
        setMasterComparison(createEmptyMaterialMasterComparison());
        setMismatchOpen(false);
        await loadSuggestions();
        return;
      }

      setMasterComparison((current) => ({
        ...current,
        materialNumber,
        status: "loading",
        message: null,
      }));
      setServiceStatus("masterData", "checking", "Material wird geprüft");
      setScanDetails((current) =>
        current
          ? appendOrReplaceProcessStep(
              current,
              "master-data",
              "Materialstammliste abgleichen",
              "running",
              `Materialnummer ${materialNumber}`
            )
          : current
      );

      try {
        const { record, snapshot } = await findMaterialByNumber(materialNumber);
        if (cancelled) return;

        if (!record) {
          const detail =
            `Materialnummer ${materialNumber} wurde gültig erkannt, ist aber ` +
            "in der aktuellen Materialstammliste nicht enthalten.";

          // Der reine OCR-/Materialstamm-Abgleich erzeugt noch keine globale
          // Meldung und keinen Admin-Eintrag. Erst wenn der Benutzer den Vorgang
          // fachlich übernimmt (Schritt 3 oder Entwurf speichern), wird das
          // unbekannte Material registriert.
          setMasterComparison({
            materialNumber,
            status: "not-found",
            record: null,
            mismatches: [],
            masterDataCreatedAt: snapshot.createdAt,
            message: detail,
          });
          setMismatchOpen(false);

          setScanDetails((current) =>
            current
              ? appendOrReplaceProcessStep(
                  current,
                  "master-data",
                  "Materialstammliste abgleichen",
                  "done",
                  "Gültige Materialnummer nicht gefunden; Materialvorschläge verfügbar.",
                  performance.now() - comparisonStartedAt
                )
              : current
          );

          // Auch bei formal gültiger, aber unbekannter Materialnummer
          // bleibt das vorhandene Materialvorschläge verfügbar.
          await loadSuggestions();
          return;
        }

        clearIssue("master-error");
        clearIssue(`unknown-material-${materialNumber}`);
        setSuggestions([]);
        setSuggestionTotal(0);
        setSuggestionPage(0);
        setSuggestionsOpen(false);

        const mismatches = buildMismatches(
          labelData,
          record.description,
          record.type,
          record.storageLocations
        );
        const nextComparison: MaterialMasterComparison = {
          materialNumber,
          status: mismatches.length > 0 ? "mismatch" : "match",
          record,
          mismatches,
          masterDataCreatedAt: snapshot.createdAt,
          message: null,
        };

        setServiceStatus("masterData", "ok", "Materialstammliste verfügbar");
        setMasterComparison(nextComparison);

        if (mismatches.length > 0) {
          setRememberedMismatch(nextComparison);
        }

        if (autoMismatchEligibleRef.current) {
          if (mismatches.length > 0) setMismatchOpen(true);
          autoMismatchEligibleRef.current = false;
        }

        setScanDetails((current) =>
          current
            ? appendOrReplaceProcessStep(
                current,
                "master-data",
                "Materialstammliste abgleichen",
                "done",
                mismatches.length > 0
                  ? `${mismatches.length} Abweichung(en) gefunden.`
                  : "Keine Abweichung gefunden.",
                performance.now() - comparisonStartedAt
              )
            : current
        );
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          error instanceof Error
            ? error.message
            : "Materialstammliste konnte nicht geprüft werden.";
        setServiceStatus("masterData", "error", "Nicht verfügbar");
        reportIssue({
          id: "master-error",
          severity: "error",
          title: "Materialstammliste nicht verfügbar",
          detail: message,
        });
        setMasterComparison({
          materialNumber,
          status: "error",
          record: null,
          mismatches: [],
          masterDataCreatedAt: null,
          message,
        });
      }
    }

    const timer = window.setTimeout(() => {
      void runComparison();
    }, 550);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [step, labelData, reportIssue, clearIssue, setServiceStatus]);

  function updateLabelData(
    field: keyof RecognizedLabelData,
    value: string
  ): void {
    autoMismatchEligibleRef.current = false;
    if (field !== "orderInfo" && visionFeedbackBaselineRef.current) {
      visionFeedbackInterventionsRef.current[field] = "manual";
    }
    setSuggestionPage(0);
    setLabelData((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function advanceCaptureToStep2(): void {
    // Vorwärtsnavigation im Workflow: Kamera-Layer bleibt als vorherige
    // History-Ebene erhalten, der neue Step bekommt seinen eigenen Sentinel.
    // Kein history.back() und kein Abbruch-onClose beim erfolgreichen Capture.
    setCameraOpen(false);
    setStep(2);
  }

  function completeStep2RecognitionTiming(details: ScanCaptureDetails | null): ScanCaptureDetails | null {
    if (!details?.recognitionTiming) return details;
    return {
      ...details,
      recognitionTiming: completeRecognitionRun(details.recognitionTiming),
    };
  }

  function startManualWithdrawal(): void {
    if (voSimulation || continuedWorkflow !== null || editWithdrawalId !== null) return;
    ocrGenerationRef.current += 1;
    if (!withdrawalDate) setWithdrawalDate(new Date().toISOString().slice(0, 10));
    setManualWithdrawal(true);
    setVoSimulation(false);
    setVoCaptureClientUuid(null);
    setContinuedWorkflow(null);
    autoMismatchEligibleRef.current = false;
    setQuantityRemoved("");
    setCurrentStock("");
    setSelectedPersons([]);
    setPendingNewNames([]);
    setSelectedAllocation(null);
    setStep2ValidationAttempted(false);
    setStep3ValidationAttempted(false);
    if (controlPreviewUrl) URL.revokeObjectURL(controlPreviewUrl);
    setControlPreviewUrl(null);
    setControlFile(null);
    setAnalysisFile(null);
    setSourceFile(null);
    setAnalysisInputFile(null);
    setRectificationInputFile(null);
    setLiveTrainingFile(null);
    setOriginalCaptureFile(null);
    setScanDetails(null);
    setOcrDetails(createEmptyOcrDetails());
    setLabelData(createEmptyRecognizedLabelData());
    setMasterComparison(createEmptyMaterialMasterComparison());
    setRememberedMismatch(null);
    setSuggestions([]);
    setSuggestionTotal(0);
    setSuggestionPage(0);
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    visionRetakesRef.current = [];
    step2ConfirmedAtRef.current = null;
    resetStep2Uncertainty();
    clearIssue("scan-no-label");
    clearIssue("ocr-error");
    clearIssue("master-not-found");
    clearIssue("master-error");
    setActivity(null);
    setNotice(null);
    setDetailsOpen(false);
    setMismatchOpen(false);
    setSuggestionsOpen(false);
    setCameraOpen(false);
    setStep(2);
  }

  async function handleCapture(
    nextControlFile: File,
    nextAnalysisFile?: File,
    nextDetails?: ScanCaptureDetails,
    nextSourceFile?: File,
    frozenPreviewFile?: File,
    nextOriginalCaptureFile?: File,
    nextLiveTrainingFile?: File,
    nextAnalysisInputFile?: File,
    nextRectificationInputFile?: File,
    nextRectificationInputArchiveFactory?: () => Promise<{ file: File | null; elapsedMs: number | null }>,
    precomputedFinalOcr?: PrecomputedFinalOcrPayload | null,
  ): Promise<void> {
    if (!withdrawalDate) setWithdrawalDate(new Date().toISOString().slice(0, 10));
    setContinuedWorkflow(null);
    autoMismatchEligibleRef.current = true;
    setQuantityRemoved("");
    setCurrentStock("");
    setSelectedPersons([]);
    setPendingNewNames([]);
    setSelectedAllocation(null);
    setAllocationQuery("");
    setAllocationResults([]);
    setAllocationTotal(0);
    setAllocationPage(0);
    setDraftClientUuid(null);
    setDraftWithdrawalNumber(null);
    setStep2ValidationAttempted(false);
    setStep3ValidationAttempted(false);
    const generation = ++ocrGenerationRef.current;
    const baseDetails = nextDetails ? { ...nextDetails, ocr: null } : null;
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    step2ConfirmedAtRef.current = null;
    resetStep2Uncertainty();

    if (baseDetails?.detection) {
      void recordVisualLabQualityEvent({
        found: baseDetails.detection.found && baseDetails.detection.finalGeometryAccepted,
        processing_time_ms: baseDetails.detection.processingTimeMs,
        confidence: baseDetails.detection.score,
        method: baseDetails.detection.detectionMethod,
      });
    }

    if (controlPreviewUrl) {
      URL.revokeObjectURL(controlPreviewUrl);
    }
    void frozenPreviewFile;
    const nextControlUrl = URL.createObjectURL(nextControlFile);
    setControlPreviewUrl(nextControlUrl);
    setActivity("Etikett wird verarbeitet …");
    setControlFile(nextControlFile);
    setAnalysisFile(nextAnalysisFile ?? null);
    setSourceFile(nextSourceFile ?? null);
    setAnalysisInputFile(nextAnalysisInputFile ?? null);
    setRectificationInputFile(nextRectificationInputFile ?? null);
    setLiveTrainingFile(nextLiveTrainingFile ?? null);
    setOriginalCaptureFile(nextOriginalCaptureFile ?? null);
    setScanDetails(baseDetails);
    setOcrDetails(createEmptyOcrDetails());
    setLabelData(createEmptyRecognizedLabelData());
    setMasterComparison(createEmptyMaterialMasterComparison());
    setRememberedMismatch(null);
    setMismatchOpen(false);
    setSuggestionsOpen(false);
    setSuggestions([]);
    setDetailsOpen(false);
    setNotice(null);

    const validAnalysis = Boolean(
      nextAnalysisFile
      && baseDetails?.detection?.found
      && baseDetails.detection.finalGeometryAccepted
      && baseDetails.rectification.used
    );

    if (!validAnalysis || !nextAnalysisFile) {
      if (baseDetails) {
        const uncertaintyDetails = appendOrReplaceProcessStep(
          baseDetails,
          "ocr",
          "Etikettfelder per OCR lesen",
          "skipped",
          "OCR übersprungen: keine vollständig akzeptierte und perspektivisch korrigierte Etikettgeometrie."
        );
        finalizeStep2Uncertainty(createEmptyRecognizedLabelData(), createEmptyOcrDetails(), uncertaintyDetails);
        const completedDetails = completeStep2RecognitionTiming(uncertaintyDetails);
        setScanDetails(completedDetails);
      }
      setActivity(null);
      advanceCaptureToStep2();
      return;
    }

    const startedAt = new Date().toISOString();
    const runningOcr: ScanOcrDetails = {
      ...createEmptyOcrDetails(),
      status: "running",
      startedAt,
      progress: 0,
    };
    setOcrDetails(runningOcr);
    if (baseDetails) {
      setScanDetails(appendOrReplaceProcessStep(
        baseDetails,
        "ocr",
        "Etikettfelder per OCR lesen",
        "running",
        "Perspektivisch korrigiertes Analysebild wird gelesen."
      ));
    }

    let postStep2ResearchTask: (() => void) | null = null;

    try {
      // PV-9_PRODUCTION_FINAL_OCR_BEGIN
      const progressCallback = (progress: number) => {
        if (ocrGenerationRef.current !== generation) return;
        setOcrDetails((current) => ({ ...current, progress }));
      };
      const { evaluateParallelProductionRuntimeNow, tripParallelProductionSessionFuse } = await import("../services/parallelProductionRuntimeService");
      const productionDecision = await evaluateParallelProductionRuntimeNow();
      const productionEvidenceBase = {
        schemaVersion: 1 as const,
        contractVersion: "gated-production-final-ocr-v1" as const,
        fullFidelityPreserved: true as const,
        activationGateSatisfied: productionDecision.activationGateSatisfied,
        admissionAllowed: productionDecision.admissionAllowed,
        probeReady: productionDecision.probeReady,
        poolsWarm: productionDecision.poolsWarm,
        sessionFuseTripped: productionDecision.sessionFuseTripped,
        requestedWorkers: 2 as const,
      };

      let result: Awaited<ReturnType<typeof recognizeLabelData>>;
      let productionOcrEvidence: ScanOcrParallelProductionEvidence;
      const reusablePv15FinalOcr = productionDecision.allowed
        && precomputedFinalOcr?.contractVersion === "pv15-speculative-final-ocr-v1"
        && precomputedFinalOcr.sourceFile === nextAnalysisFile
        && precomputedFinalOcr.recognitionRunId === (baseDetails?.recognitionTiming?.recognitionRunId ?? null);

      if (reusablePv15FinalOcr && precomputedFinalOcr) {
        const parallel = precomputedFinalOcr.execution;
        progressCallback(1);
        productionOcrEvidence = {
          ...productionEvidenceBase,
          profileId: "pv9-parallel-final-ocr-production-v1",
          status: "parallel",
          productionInfluenced: true,
          reason: null,
          effectiveWorkers: parallel.effectiveWorkers,
          activeWorkersPeak: parallel.activeWorkersPeak,
          workerStartupMs: parallel.workerStartupMs,
          parallelWallMs: parallel.parallelWallMs,
          overlapMs: parallel.overlapMs,
          overlapRatio: parallel.overlapRatio,
          mergeOrder: parallel.mergeOrder,
          tasks: parallel.tasks,
          serialFallbackUsed: false,
          error: null,
        };
        result = {
          ...parallel.result,
          details: {
            ...parallel.result.details,
            parallelProduction: productionOcrEvidence,
          },
        };
        if (baseDetails?.criticalPathOrchestration) {
          baseDetails.criticalPathOrchestration = {
            ...baseDetails.criticalPathOrchestration,
            speculativeFinalOcrStatus: "reused",
          };
        }
      } else if (productionDecision.allowed) {
        try {
          const service = await getLabelOcrService();
          const parallel = await service.recognizeLabelDataParallelProduction(
            nextAnalysisFile,
            progressCallback,
            baseDetails?.detection?.visionSnapshot,
            { recognitionRunId: baseDetails?.recognitionTiming?.recognitionRunId ?? null },
          );
          productionOcrEvidence = {
            ...productionEvidenceBase,
            profileId: "pv9-parallel-final-ocr-production-v1",
            status: "parallel",
            productionInfluenced: true,
            reason: null,
            effectiveWorkers: parallel.effectiveWorkers,
            activeWorkersPeak: parallel.activeWorkersPeak,
            workerStartupMs: parallel.workerStartupMs,
            parallelWallMs: parallel.parallelWallMs,
            overlapMs: parallel.overlapMs,
            overlapRatio: parallel.overlapRatio,
            mergeOrder: parallel.mergeOrder,
            tasks: parallel.tasks,
            serialFallbackUsed: false,
            error: null,
          };
          result = {
            ...parallel.result,
            details: {
              ...parallel.result.details,
              parallelProduction: productionOcrEvidence,
            },
          };
        } catch (parallelError: unknown) {
          const message = parallelError instanceof Error ? parallelError.message : String(parallelError);
          tripParallelProductionSessionFuse(message);
          const serial = await recognizeLabelData(
            nextAnalysisFile,
            progressCallback,
            undefined,
            undefined,
            baseDetails?.detection?.visionSnapshot,
          );
          productionOcrEvidence = {
            ...productionEvidenceBase,
            profileId: "serial-production-v1",
            status: "serial_fallback",
            productionInfluenced: false,
            sessionFuseTripped: true,
            reason: "PV9_PARALLEL_OCR_RUNTIME_ERROR",
            effectiveWorkers: 0,
            activeWorkersPeak: 0,
            workerStartupMs: null,
            parallelWallMs: null,
            overlapMs: null,
            overlapRatio: null,
            mergeOrder: ["materialNumber", "storageAddress", "materialType", "materialText"],
            tasks: [],
            serialFallbackUsed: true,
            error: message,
          };
          result = {
            ...serial,
            details: {
              ...serial.details,
              parallelProduction: productionOcrEvidence,
            },
          };
        }
      } else {
        const serial = await recognizeLabelData(
          nextAnalysisFile,
          progressCallback,
          undefined,
          undefined,
          baseDetails?.detection?.visionSnapshot,
        );
        productionOcrEvidence = {
          ...productionEvidenceBase,
          profileId: "serial-production-v1",
          status: "not_eligible",
          productionInfluenced: false,
          reason: productionDecision.reason,
          effectiveWorkers: 0,
          activeWorkersPeak: 0,
          workerStartupMs: null,
          parallelWallMs: null,
          overlapMs: null,
          overlapRatio: null,
          mergeOrder: ["materialNumber", "storageAddress", "materialType", "materialText"],
          tasks: [],
          serialFallbackUsed: false,
          error: null,
        };
        result = {
          ...serial,
          details: {
            ...serial.details,
            parallelProduction: productionOcrEvidence,
          },
        };
      }
      // PV-9_PRODUCTION_FINAL_OCR_END
      if (ocrGenerationRef.current !== generation) return;

      clearIssue("scan-no-label");
      clearIssue("ocr-error");

      // Production-Erkennung bleibt vollständig bildbasiert. Der Materialstamm
      // darf vor Schritt 2 weder OCR-Werte ersetzen noch die VO-Baseline
      // "schönfüllen". Vergleich, Fuzzy-Vorschläge und bewusste Übernahme
      // bleiben ausschließlich im bereits vorhandenen Schritt-2-Dialog.
      const resolvedData = { ...result.data, orderInfo: "" };
      const resolvedOcrDetails = result.details;

      setLabelData(resolvedData);
      setOcrDetails(resolvedOcrDetails);
      const multiPassFields = resolvedOcrDetails.fields.filter((field) => (field.passCount ?? 1) > 1).length;
      const gridText = resolvedOcrDetails.gridAnchoring?.applied ? " · Grid-ROI" : "";
      const multiPassText = multiPassFields > 0 ? ` · ${multiPassFields} Multi-Pass` : "";
      const uncertaintyDetails = baseDetails
        ? appendOrReplaceProcessStep(
            { ...baseDetails, ocr: resolvedOcrDetails },
            "ocr",
            "Etikettfelder per OCR lesen",
            "done",
            `${resolvedOcrDetails.fields.filter((field) => field.text.length > 0).length}/${resolvedOcrDetails.fields.length} Felder erkannt${gridText}${multiPassText}`,
            resolvedOcrDetails.durationMs
          )
        : null;
      const feedbackBaseline = createVisionFeedbackBaseline(
        resolvedData,
        resolvedOcrDetails,
        uncertaintyDetails,
      );
      const completedDetails = completeStep2RecognitionTiming(uncertaintyDetails);
      feedbackBaseline.recognitionTiming = completedDetails?.recognitionTiming ?? null;
      visionFeedbackBaselineRef.current = feedbackBaseline;
      setScanDetails(completedDetails);

      // PV-4_POST_STEP2_RESEARCH_BEGIN
      // Evidence-Materialisierung und beide Parallel-Shadows starten erst NACH
      // der Step-2-Navigation. Recognition-Truth ist zu diesem Zeitpunkt final.
      // Das lossless Rectification-Input-PNG wird damit vollständig aus dem
      // Shutter→Step-2-Critical-Path entfernt.
      postStep2ResearchTask = () => {
        void (async () => {
          let detailsForShadows = completedDetails;
          let archiveFile = nextRectificationInputFile ?? null;

          if (!archiveFile && nextRectificationInputArchiveFactory) {
            try {
              const archive = await nextRectificationInputArchiveFactory();
              if (ocrGenerationRef.current !== generation) return;
              archiveFile = archive.file;
              if (archive.file) setRectificationInputFile(archive.file);
              if (detailsForShadows?.rectification.timing) {
                detailsForShadows = {
                  ...detailsForShadows,
                  rectification: {
                    ...detailsForShadows.rectification,
                    timing: {
                      ...detailsForShadows.rectification.timing,
                      inputArchiveElapsedMs: archive.elapsedMs,
                      inputArchiveMode: "post-step2-deferred",
                      inputArchiveCriticalPathWaitMs: 0,
                    },
                  },
                };
                setScanDetails((current) => current?.rectification.timing
                  ? {
                      ...current,
                      rectification: {
                        ...current.rectification,
                        timing: {
                          ...current.rectification.timing,
                          inputArchiveElapsedMs: archive.elapsedMs,
                          inputArchiveMode: "post-step2-deferred",
                          inputArchiveCriticalPathWaitMs: 0,
                        },
                      },
                    }
                  : current);
              }
            } catch {
              archiveFile = null;
            }
          }

          const openCvShadow = await scheduleOpenCvParallelShadow({
            rectificationInputFile: archiveFile,
            rawPhotoFile: nextOriginalCaptureFile ?? nextSourceFile ?? null,
            scanDetails: detailsForShadows,
            recognitionRunId: completedDetails?.recognitionTiming?.recognitionRunId ?? null,
          });
          if (ocrGenerationRef.current !== generation) return;
          setScanDetails((current) => current
            ? { ...current, rectification: { ...current.rectification, parallelShadow: openCvShadow } }
            : current);
          const detailsWithOpenCvShadow = detailsForShadows
            ? { ...detailsForShadows, rectification: { ...detailsForShadows.rectification, parallelShadow: openCvShadow } }
            : detailsForShadows;
          // Sobald PV-9 die finale Vier-Feld-OCR produktiv parallel ausführt,
          // wäre ein weiterer PV-2 Parallel-vs-"Serial"-Shadow fachlich falsch,
          // weil `result` dann bereits aus dem Parallelpfad stammt. Das 10/10-Gate
          // wurde vor dem Cutover erreicht; der laufende Production-Run bleibt
          // über seine eigene PV-9-Evidence transparent.
          const productionFinalOcrParallel = result.details.parallelProduction?.status === "parallel";
          const parallelShadow = productionFinalOcrParallel
            ? null
            : await scheduleRecognitionParallelShadow({
                file: nextAnalysisFile,
                serialResult: result,
                scanDetails: detailsWithOpenCvShadow,
                runtimeSnapshot: baseDetails?.detection?.visionSnapshot,
                recognitionRunId: completedDetails?.recognitionTiming?.recognitionRunId ?? null,
              });
          if (ocrGenerationRef.current !== generation) return;
          setOcrDetails((current) => current.status === "done"
            ? { ...current, parallelShadow }
            : current);
          const { recordParallelRuntimeSoakScan } = await import("../services/parallelRuntimeSoakService");
          const detailsWithBothShadows = detailsWithOpenCvShadow?.ocr?.status === "done"
            ? { ...detailsWithOpenCvShadow, ocr: { ...detailsWithOpenCvShadow.ocr, parallelShadow } }
            : detailsWithOpenCvShadow;
          let parallelRuntimeSoak = recordParallelRuntimeSoakScan({
            scanDetails: detailsWithBothShadows,
            openCvShadow,
            ocrShadow: parallelShadow,
          });
          if (voSimulation && parallelRuntimeSoak.gates.cleanParityWindow) {
            const { ensureParallelRuntimeAcceptanceEvidence } = await import("../services/parallelRuntimeAcceptanceService");
            await ensureParallelRuntimeAcceptanceEvidence();
            parallelRuntimeSoak = getParallelRuntimeSoakSnapshot();
          }
          setScanDetails((current) => current?.ocr?.status === "done"
            ? { ...current, ocr: { ...current.ocr, parallelShadow }, parallelRuntimeSoak }
            : current);
          const baseline = visionFeedbackBaselineRef.current;
          if (baseline && baseline.capturedAt === feedbackBaseline.capturedAt) {
            visionFeedbackBaselineRef.current = {
              ...baseline,
              ocrDetails: { ...baseline.ocrDetails, parallelShadow: structuredClone(parallelShadow) },
            };
          }
        })().catch(() => undefined);
      };
      // PV-4_POST_STEP2_RESEARCH_END
    } catch (error: unknown) {
      if (ocrGenerationRef.current !== generation) return;
      const message = error instanceof Error ? error.message : "OCR konnte nicht ausgeführt werden.";
      const failedDetails: ScanOcrDetails = {
        ...createEmptyOcrDetails(),
        status: "error",
        startedAt,
        finishedAt: new Date().toISOString(),
        error: message,
      };
      setOcrDetails(failedDetails);
      const uncertaintyDetails = baseDetails
        ? appendOrReplaceProcessStep(
            { ...baseDetails, ocr: failedDetails },
            "ocr",
            "Etikettfelder per OCR lesen",
            "error",
            message
          )
        : null;
      if (uncertaintyDetails) {
        finalizeStep2Uncertainty(createEmptyRecognizedLabelData(), failedDetails, uncertaintyDetails);
      }
      const failedScanDetails = completeStep2RecognitionTiming(uncertaintyDetails);
      setScanDetails(failedScanDetails);
    } finally {
      if (ocrGenerationRef.current === generation) {
        setActivity(null);
        advanceCaptureToStep2();
        if (postStep2ResearchTask) {
          window.setTimeout(postStep2ResearchTask, 120);
        }
      }
    }
  }

  function handleRetake(): void {
    setManualWithdrawal(false);
    visionRetakesRef.current = [
      ...visionRetakesRef.current,
      createRetakeSignal(scanDetails, ocrDetails, labelData),
    ].slice(-8);
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    step2ConfirmedAtRef.current = null;
    resetStep2Uncertainty();
    ocrGenerationRef.current += 1;
    if (controlPreviewUrl) {
      URL.revokeObjectURL(controlPreviewUrl);
      setControlPreviewUrl(null);
    }
    setSourceFile(null);
    setAnalysisInputFile(null);
    setRectificationInputFile(null);
    setLiveTrainingFile(null);
    setOriginalCaptureFile(null);
    clearIssue("scan-no-label");
    clearIssue("ocr-error");
    clearIssue("master-not-found");
    clearIssue("master-error");
    setActivity(null);
    setNotice(null);
    setMismatchOpen(false);
    setSuggestionsOpen(false);
    setSuggestions([]);
    setDetailsOpen(false);
    setCameraOpen(true);
  }

  function registerUnknownMaterialIfCommitted(): void {
    const materialNumber = labelData.materialNumber.trim();
    if (!/^983\d{7}$/.test(materialNumber) || masterComparison.status !== "not-found") return;
    const workflowKey = materialNumber;
    if (reportedUnknownMaterialsRef.current.has(workflowKey)) return;
    reportedUnknownMaterialsRef.current.add(workflowKey);

    const detail = `Materialnummer ${materialNumber} ist in der aktuellen Materialstammliste nicht enthalten.`;
    if (getLastKnownAuthUser()?.role !== "admin") {
      reportIssue({
        id: `unknown-material-${materialNumber}`,
        severity: "warning",
        title: "Unbekanntes Material",
        detail,
      });
    } else {
      clearIssue(`unknown-material-${materialNumber}`);
    }
    void fetch("/api/notices/unknown-material", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        material_number: materialNumber,
        material_text: labelData.materialText.trim() || null,
        material_type: labelData.materialType.trim() || null,
        storage_address: isValidStorageAddress(labelData.storageAddress) ? labelData.storageAddress : null,
      }),
    }).catch(() => {
      // Die Entnahme darf durch eine nicht erreichbare Admin-Meldung niemals blockieren.
      reportedUnknownMaterialsRef.current.delete(workflowKey);
    });
  }

  function handleContinue(): void {
    setNotice(null);
    setStep2ValidationAttempted(true);
    if (!canContinueStep2) return;
    step2ConfirmedAtRef.current = new Date().toISOString();
    registerUnknownMaterialIfCommitted();
    setStep(3);
  }


  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;

    const applyNames = (names: SelectableUserName[]) => {
      if (cancelled) return;
      setAvailableNames(names);
      const favorite = names.find((item) => item.is_favorite);
      if (favorite) {
        setSelectedPersons((current) => current.length === 0 ? [favorite.name] : current);
      }
    };

    // Local-first: bekannte Namen und Favorit stehen sofort zur Verfügung.
    const cached = getCachedUserNames();
    if (cached.length > 0) applyNames(cached);

    void fetchUserNames()
      .then((names) => applyNames(names))
      .catch(() => {
        // Ohne Server bleibt der zuletzt bestätigte lokale Cache sichtbar.
        if (!cancelled && cached.length === 0) setAvailableNames([]);
      });

    return () => { cancelled = true; };
  }, [step]);

  async function searchAllocations(value: string, page = 0): Promise<void> {
    setAllocationQuery(value);
    setAllocationPage(page);
    try {
      const result = await searchAllocationMasterData(
        value,
        allocationPageSize,
        page * allocationPageSize
      );
      setAllocationResults(result.records);
      setAllocationTotal(result.total);
    } catch {
      setAllocationResults([]);
      setAllocationTotal(0);
    }
  }

  function createTemporaryPerson(name: string): void {
    const clean = name.trim().replace(/\s+/g, " ");
    if (!clean) return;
    const existing = availableNames.some((item) => item.name.toLocaleLowerCase("de-DE") === clean.toLocaleLowerCase("de-DE"));
    if (!existing) {
      setAvailableNames((current) => [
        ...current,
        { id: -(Date.now()), name: clean, is_favorite: false },
      ]);
      setPendingNewNames((current) => current.some((item) => item.toLocaleLowerCase("de-DE") === clean.toLocaleLowerCase("de-DE")) ? current : [...current, clean]);
    }
    setSelectedPersons((current) => current.includes(clean) ? current : [...current, clean]);
  }

  async function persistSelectedNewNames(): Promise<void> {
    const namesToPersist = pendingNewNames.filter((name) => selectedPersons.includes(name));
    if (namesToPersist.length === 0) return;
    const failures: string[] = [];
    for (const name of namesToPersist) {
      try {
        const response = await fetch("/api/settings/names", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        });
        if (!response.ok && response.status !== 409) failures.push(name);
      } catch {
        failures.push(name);
      }
    }
    if (failures.length > 0) {
      reportIssue({ id: "new-names-not-saved", severity: "warning", title: "Neue Namen nicht vollständig gespeichert", detail: `Nicht gespeichert: ${failures.join(", ")}` });
    } else {
      clearIssue("new-names-not-saved");
      setPendingNewNames([]);
    }
  }

  function selectPerson(name: string): void {
    const clean = name.trim();
    if (!clean) return;
    setSelectedPersons((current) =>
      current.includes(clean)
        ? current.filter((item) => item !== clean)
        : [...current, clean]
    );
  }

  const documentData = useCallback(() => ({
    withdrawalDate: withdrawalDate || new Date().toISOString().slice(0, 10),
    materialNumber: labelData.materialNumber.trim(),
    materialText: labelData.materialText.trim(),
    materialType: labelData.materialType.trim(),
    storageAddress: labelData.storageAddress.trim(),
    quantityRemoved: quantityRemoved.trim(),
    currentStock: currentStock.trim(),
    persons: [...selectedPersons],
    accountingNumber: selectedAllocation?.orderNumber ?? "",
    accountingDescription: selectedAllocation ? `${selectedAllocation.plant} - ${selectedAllocation.plantArea}` : "",
    orderInfo: labelData.orderInfo.trim(),
  }), [currentStock, labelData, quantityRemoved, selectedAllocation, selectedPersons, withdrawalDate]);

  const currentDocumentSnapshot = useCallback(() => {
    const currentData = documentData();
    const data = { ...currentData, persons: [...currentData.persons] };
    const allocation = selectedAllocation ? { ...selectedAllocation } : null;
    const signature = JSON.stringify({
      ...data,
      allocationPlant: allocation?.plant ?? null,
      allocationArea: allocation?.plantArea ?? null,
      allocationManufacturer: allocation?.manufacturer ?? null,
    });
    return { data, allocation, signature };
  }, [documentData, selectedAllocation]);

  const currentDraftSignature = useCallback((): string => currentDocumentSnapshot().signature, [currentDocumentSnapshot]);

  const draftAlreadySaved = draftClientUuid !== null && lastSavedDraftSignature === currentDraftSignature();

  const cachePreparedArtifacts = useCallback((snapshot = currentDocumentSnapshot()) => {
    // Der Cache besitzt nicht nur eine Signatur, sondern auch exakt den eingefrorenen
    // Formular-Snapshot, aus dem die PDF/WebP-Promise erzeugt wurde. Dadurch darf ein
    // Commit denselben bereits laufenden/fertigen Render sicher übernehmen, ohne je
    // Artefakte eines älteren Formularstands zu verwenden.
    const frozenData = { ...snapshot.data, persons: [...snapshot.data.persons] };
    const startedAtMs = performance.now();
    const promise = createWithdrawalDocumentArtifacts(frozenData).catch((error) => {
      if (preparedDocumentRef.current?.promise === promise) preparedDocumentRef.current = null;
      throw error;
    });
    preparedDocumentRef.current = { signature: snapshot.signature, promise, startedAtMs };
    return promise;
  }, [currentDocumentSnapshot]);

  function prepareStep4Artifacts(): void {
    const snapshot = currentDocumentSnapshot();
    const prepared = preparedDocumentRef.current;
    if (prepared?.signature === snapshot.signature) return;
    void cachePreparedArtifacts(snapshot);
  }

  const preparedArtifacts = useCallback((signature: string) => {
    const prepared = preparedDocumentRef.current;
    if (prepared?.signature === signature) return prepared.promise;
    const snapshot = currentDocumentSnapshot();
    return cachePreparedArtifacts(snapshot);
  }, [cachePreparedArtifacts, currentDocumentSnapshot]);

  async function createFreshCommitArtifacts() {
    // Save/Finalize friert den sichtbaren Formularstand einmal atomar ein. Gibt es
    // bereits eine vorgewärmte PDF/WebP-Promise mit exakt derselben Signatur, wird
    // genau diese Promise übernommen. Bei jeder noch so kleinen Formularänderung
    // entsteht dagegen weiterhin zwingend eine frische Dokumentgeneration.
    const snapshot = currentDocumentSnapshot();
    const prepared = preparedDocumentRef.current;
    const prewarmCacheHit = prepared?.signature === snapshot.signature;
    const waitStartedAtMs = performance.now();
    const artifacts = prewarmCacheHit
      ? await prepared.promise
      : await cachePreparedArtifacts(snapshot);
    const prewarmWaitMs = Math.round(performance.now() - waitStartedAtMs);
    preparedDocumentRef.current = {
      signature: snapshot.signature,
      promise: Promise.resolve(artifacts),
      startedAtMs: prewarmCacheHit ? prepared.startedAtMs : waitStartedAtMs,
    };
    return { ...snapshot, artifacts, prewarmCacheHit, prewarmWaitMs };
  }

  const documentPrewarmSignature = currentDraftSignature();
  useEffect(() => {
    if (step !== 2 && step !== 3) return;
    const signature = documentPrewarmSignature;
    const timer = window.setTimeout(() => {
      const idleWindow = window as typeof window & { requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number };
      const run = () => { if (currentDraftSignature() === signature) void preparedArtifacts(signature).catch(() => undefined); };
      if (idleWindow.requestIdleCallback) idleWindow.requestIdleCallback(run, { timeout: 500 }); else run();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [currentDraftSignature, documentPrewarmSignature, preparedArtifacts, step]);

  async function attachDiagnosticsAndStartSync(
    recordUuid: string,
    fallbackRecord: Awaited<ReturnType<typeof createWithdrawal>>,
    diagnosticsPromise: ReturnType<typeof createAdminDiagnostics>,
  ): Promise<void> {
    let current = await getLocalWithdrawal(recordUuid).catch(() => null) ?? fallbackRecord;
    const expectedRevision = current.content_revision ?? 0;

    // Der fachliche Sync startet sofort. Admin-Diagnose ist Best-Effort und darf
    // weder die Abschlussseite noch Metadaten-/PDF-/Mail-Synchronisierung blockieren.
    // Genau dadurch bleibt „Lokal gespeichert“ nicht mehr bis zum Debug-Render stehen.
    const syncPromise = typeof navigator !== "undefined" && navigator.onLine
      ? syncSingleWithdrawal(current).catch(() => undefined)
      : Promise.resolve();

    try {
      const diagnostics = await diagnosticsPromise;
      const latest = await getLocalWithdrawal(recordUuid).catch(() => null) ?? current;
      // Eine später gespeicherte Bearbeitung/Finalisierung gewinnt immer. Reine
      // Diagnose darf content_revision/sync_status NICHT verändern und dadurch
      // keinen bereits laufenden bzw. abgeschlossenen Vollsync erneut auslösen.
      if ((latest.content_revision ?? 0) === expectedRevision && latest.record_type === current.record_type) {
        const diagnosticNumber = latest.local_withdrawal_number ?? latest.server_withdrawal_number;
        const diagnosticPrefix = diagnosticNumber ? `#${diagnosticNumber}_` : "";
        const prefixDiagnosticName = (name: string | null | undefined) => name && diagnosticPrefix && !name.startsWith(diagnosticPrefix) ? `${diagnosticPrefix}${name.replace(/^#\d+_/, "")}` : name ?? null;
        const stored = await compareAndSwapLocalWithdrawal(recordUuid, expectedRevision, (fresh) => {
          if (!fresh || fresh.record_type !== current.record_type) return fresh;
          return {
            ...fresh,
            debug_image_blob: diagnostics.image ?? fresh.debug_image_blob ?? null,
            debug_image_filename: prefixDiagnosticName(diagnostics.imageName) ?? fresh.debug_image_filename ?? null,
            debug_data_blob: diagnostics.data ?? fresh.debug_data_blob ?? null,
            debug_data_filename: prefixDiagnosticName(diagnostics.dataName) ?? fresh.debug_data_filename ?? null,
          };
        });
        if (stored) {
          current = stored;
          await saveRecoverySnapshot(stored).catch(() => undefined);
        }
      } else {
        current = latest;
      }

      // Falls der Hauptsync die Diagnosephase schon passiert hatte, werden die
      // optionalen Dateien nach seiner Quittierung direkt best-effort nachgeladen.
      // Der Server ersetzt pro Version/Dateityp atomar, daher entstehen keine
      // doppelten fachlichen Timeline-/Sync-Ereignisse.
      await syncPromise;
      const afterSync = await getLocalWithdrawal(recordUuid).catch(() => null) ?? current;
      if (typeof navigator !== "undefined" && navigator.onLine && afterSync.server_withdrawal_id) {
        if (diagnostics.image && afterSync.debug_image_filename) {
          await uploadWithdrawalFileRaw(afterSync.server_withdrawal_id, "debug", diagnostics.image, afterSync.debug_image_filename).catch(() => undefined);
        }
        if (diagnostics.data && afterSync.debug_data_filename) {
          await uploadWithdrawalFileRaw(afterSync.server_withdrawal_id, "debug_data", diagnostics.data, afterSync.debug_data_filename).catch(() => undefined);
        }
      }
    } catch {
      // Diagnose bleibt vollständig optional. Der bereits gestartete fachliche
      // Sync läuft unabhängig weiter und darf hier niemals zurückgesetzt werden.
      await syncPromise;
    }
  }

  async function openDocumentPreview(): Promise<void> {
    setPreviewOpen(true);
    setDocumentPreviewLoading(true);
    try {
      // Vorschau, lokaler Draft, finaler Sync und E-Mail-Anhang müssen aus exakt
      // derselben validierten Dokumentgeneration stammen. Kein separater zweiter
      // Canvas-Render mehr nur für die UI-Vorschau.
      const artifacts = await preparedArtifacts(currentDraftSignature());
      const nextUrl = URL.createObjectURL(artifacts.preview);
      setDocumentPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return nextUrl;
      });
    } catch (error: unknown) {
      setNotice(error instanceof Error ? error.message : "Vorschau konnte nicht erzeugt werden.");
    } finally {
      setDocumentPreviewLoading(false);
    }
  }

  async function createAdminDiagnostics(
    materialNumber: string,
    withdrawalDateValue: string,
    identity?: { withdrawalNumber: number | null; serverId: number | null; clientUuid: string | null; caseRef?: string | null; captureOrigin?: "production_withdrawal" | "vo_bootstrap" },
  ): Promise<{ image: Blob | null; data: Blob | null; imageName?: string; dataName?: string }> {
    const safeMaterial = materialNumber.trim() || "Material";
    const fallbackImage = controlFile ?? sourceFile ?? null;
    const diagnosticScanDetails = scanDetails ? { ...scanDetails, identity } : null;
    // Diagnose-JSON und Debug-Bild verwenden denselben ScanDetails-Datensatz.
    // Zusätzlich wird der tatsächlich lokal verwendete Vision-Profil-/Parameterstand
    // eingefroren. Damit kann der spätere Server-Replay die historische Baseline
    // reproduzieren, auch wenn sich das aktive Profil inzwischen geändert hat.
    const debugData = new Blob([JSON.stringify({
      identity: identity ?? null,
      scan_details: diagnosticScanDetails,
      last_capture_failure: diagnosticScanDetails ? null : (() => {
        const flight = getLastScanFailureFlight();
        return flight?.status === "failure" ? flight : null;
      })(),
      last_capture_flight: getLastScanFailureFlight(),
      label_data: labelData,
      runtime_execution_profile: productionExecutionProfileEvidence(diagnosticScanDetails),
      runtime_parallelism_probe: recognitionParallelismEvidence(),
      runtime_full_fidelity_performance: fullFidelityPerformanceEvidence(diagnosticScanDetails),
      runtime_parallelism_soak: diagnosticScanDetails?.parallelRuntimeSoak ?? getParallelRuntimeSoakSnapshot(),
      pipeline_e2e: {
        schema_version: 1,
        system_label_data: visionFeedbackBaselineRef.current?.labelData ?? null,
        user_final_label_data: labelData,
        baseline_captured_at: visionFeedbackBaselineRef.current?.capturedAt ?? null,
      },
      app_version: APP_VERSION,
      vision_config: scanDetails?.detection?.visionSnapshot ? {
        schema_version: 2,
        source: scanDetails.detection.visionSnapshot.source,
        profile: scanDetails.detection.visionSnapshot.profileId !== null ? {
          id: scanDetails.detection.visionSnapshot.profileId,
          name: scanDetails.detection.visionSnapshot.profileName,
          stage: scanDetails.detection.visionSnapshot.profileStage,
          updated_at: scanDetails.detection.visionSnapshot.profileUpdatedAt,
          activated_at: scanDetails.detection.visionSnapshot.profileActivatedAt,
        } : null,
        runtime_api: scanDetails.detection.visionSnapshot.runtimeApi,
        runtime_compatible: scanDetails.detection.visionSnapshot.runtimeCompatible,
        runtime_compatibility_reason: scanDetails.detection.visionSnapshot.runtimeCompatibilityReason,
        bundle_id: scanDetails.detection.visionSnapshot.bundleId,
        bundle_version: scanDetails.detection.visionSnapshot.bundleVersion,
        bundle_payload_sha256: scanDetails.detection.visionSnapshot.bundlePayloadSha256,
        candidate_config_sha256: scanDetails.detection.visionSnapshot.candidateConfigSha256,
        detection_parameters: scanDetails.detection.visionSnapshot.detectionParameters,
        cnn: scanDetails.detection.visionSnapshot.cnn,
        cnn_inference: scanDetails.detection.visionSnapshot.cnnInference ?? null,
        hybrid: scanDetails.detection.visionSnapshot.hybrid,
        ocr: scanDetails.detection.visionSnapshot.ocr,
        captured_at: scanDetails.detection.visionSnapshot.capturedAt,
      } : null,
      has_control_image: Boolean(controlFile),
      has_source_image: Boolean(sourceFile),
      has_original_capture: Boolean(originalCaptureFile),
      has_detection_source: Boolean(sourceFile),
      has_analysis_input: Boolean(analysisInputFile),
      has_rectification_input: Boolean(rectificationInputFile),
      rectification_input: rectificationInputFile ? {
        name: rectificationInputFile.name,
        type: rectificationInputFile.type,
        size: rectificationInputFile.size,
        last_modified: rectificationInputFile.lastModified,
        rgba_sha256: scanDetails?.rectification?.inputRgbaSha256 ?? null,
      } : null,
      analysis_input: analysisInputFile ? {
        name: analysisInputFile.name,
        type: analysisInputFile.type,
        size: analysisInputFile.size,
        last_modified: analysisInputFile.lastModified,
        sha256: scanDetails?.detection?.analysisInputSha256 ?? null,
      } : null,
      original_capture: originalCaptureFile ? {
        name: originalCaptureFile.name,
        type: originalCaptureFile.type,
        size: originalCaptureFile.size,
        last_modified: originalCaptureFile.lastModified,
      } : null,
      debug_image_basis: diagnosticScanDetails ? "scan_details" : "fallback_capture",
      created_at: new Date().toISOString(),
    }, null, 2)], { type: "application/json" });
    let debugImage: Blob | null = fallbackImage;
    if (diagnosticScanDetails && controlFile) {
      try {
        debugImage = await buildScanDebugImage(sourceFile ?? controlFile, analysisFile, diagnosticScanDetails);
      } catch {
        // Bei fehlender/fehlerhafter Aufbereitung bleibt wenigstens die Aufnahme lokal erhalten.
        debugImage = fallbackImage;
      }
    }
    return {
      image: debugImage,
      data: debugData,
      imageName: debugImage ? `${safeMaterial}_${withdrawalDateValue}_Debug.${debugImage.type.includes("png") ? "png" : debugImage.type.includes("webp") ? "webp" : "jpg"}` : undefined,
      dataName: `${safeMaterial}_${withdrawalDateValue}_Diagnose.json`,
    };
  }

  async function saveVoCaptureAfterStep2(): Promise<void> {
    setNotice(null);
    setStep2ValidationAttempted(true);
    if (!canContinueStep2 || finalizing) return;
    step2ConfirmedAtRef.current = new Date().toISOString();
    setFinalizing(true);
    try {
      if (getLastKnownAuthUser()?.role !== "admin") {
        throw new Error("VO-Testaufnahmen sind ausschließlich für Administratoren verfügbar.");
      }
      if (!voSimulation || editWithdrawalId !== null || continuedWorkflow !== null || draftClientUuid !== null) {
        throw new Error("VO-Testaufnahme kann nur als neuer, getrennt gestarteter VO-Capture gespeichert werden.");
      }
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        throw new Error("VO-Testaufnahmen werden direkt im getrennten VO-Backend gespeichert. Dafür ist eine Serververbindung erforderlich.");
      }
      const caseUuid = voCaptureClientUuid ?? crypto.randomUUID();
      if (!voCaptureClientUuid) setVoCaptureClientUuid(caseUuid);
      const caseRef = `vo_capture:${caseUuid}`;
      const captureDate = withdrawalDate || new Date().toISOString().slice(0, 10);
      const visionFeedbackBlob = buildVisionFeedbackBlob({
        commitment: "completed",
        baseline: visionFeedbackBaselineRef.current,
        finalLabelData: labelData,
        interventions: visionFeedbackInterventionsRef.current,
        step2ConfirmedAt: step2ConfirmedAtRef.current,
        retakes: visionRetakesRef.current,
        scanDetails,
        ocrWarpAvailable: Boolean(analysisFile),
        clientUuid: caseUuid,
        withdrawalNumber: null,
        serverId: null,
      });
      const diagnostics = await createAdminDiagnostics(labelData.materialNumber, captureDate, {
        withdrawalNumber: null,
        serverId: null,
        clientUuid: caseUuid,
        caseRef,
        captureOrigin: "vo_bootstrap",
      });
      const created = await createVisionCaptureCase({
        clientUuid: caseUuid,
        workflow: {
          withdrawal_date: captureDate,
          material_number: labelData.materialNumber.trim(),
          material_text: labelData.materialText.trim(),
          material_type: labelData.materialType.trim() || null,
          storage_address: normalizeStorageAddress(labelData.storageAddress),
          quantity_removed: "",
          current_stock: "",
          accounting_system: null,
          accounting_subarea: null,
          accounting_number: null,
          accounting_manufacturer: null,
          order_info: labelData.orderInfo.trim() || null,
          persons: [],
          app_version: APP_VERSION,
        },
        artifacts: {
          label: controlFile,
          source: originalCaptureFile ?? sourceFile ?? controlFile,
          detectionSource: sourceFile ?? originalCaptureFile ?? controlFile,
          analysisInput: analysisInputFile,
          rectificationInput: rectificationInputFile,
          ocrWarp: analysisFile,
          visionFeedback: visionFeedbackBlob,
          liveTraining: liveTrainingFile,
          debugImage: diagnostics.image,
          debugData: diagnostics.data,
        },
      });
      setCompletedVoCapture(created);
      setNotice(null);
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
      void refreshVoBootstrapPolicy();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "VO-Testaufnahme konnte nicht gespeichert werden.");
    } finally {
      setFinalizing(false);
    }
  }

  async function finalizeWithdrawal(): Promise<void> {
    const location = parseStorageAddress(labelData.storageAddress);
    if (!location || !selectedAllocation || !quantityRemoved.trim() || !currentStock.trim() || selectedPersons.length === 0) return;
    setFinalizing(true);
    setCompletionDocumentsComplete(null);
    setCompletionEmailStatus(null);
    setCompletionEmailError(null);
    let createdWithdrawalId: number | null = null;
    const completionStartedAtMs = performance.now();
    const completionStartedAtIso = new Date().toISOString();
    const completionMode = editWithdrawalId !== null ? "edit" : draftClientUuid ? "finalize_draft" : "create";
    const persistenceTimings: Partial<Record<WithdrawalPersistenceTelemetryPhase, number>> = {};
    let completionDocument: Awaited<ReturnType<typeof createFreshCommitArtifacts>> | null = null;
    let completionBlobs = completionBlobBytes({});
    let completionTelemetryWritten = false;
    const persistenceTelemetry = (phase: WithdrawalPersistenceTelemetryPhase, durationMs: number) => {
      persistenceTimings[phase] = Math.round(durationMs);
    };
    const writeCompletionTelemetry = (
      status: "ok" | "error",
      record?: { client_uuid: string; server_withdrawal_number: number | null; local_withdrawal_number: number | null },
      error?: string,
    ) => {
      if (voSimulation || completionTelemetryWritten) return;
      const documentTiming = completionDocument?.artifacts.telemetry;
      saveWithdrawalCompletionTelemetry({
        ...createWithdrawalCompletionTelemetryBase(completionMode, completionStartedAtIso),
        finished_at: new Date().toISOString(),
        status,
        client_uuid: record?.client_uuid ?? draftClientUuid ?? editClientUuid ?? null,
        withdrawal_number: record?.server_withdrawal_number ?? record?.local_withdrawal_number ?? draftWithdrawalNumber ?? editWithdrawalNumber ?? null,
        prewarm_cache_hit: completionDocument?.prewarmCacheHit ?? false,
        timings_ms: {
          total: Math.round(performance.now() - completionStartedAtMs),
          prewarm_wait: completionDocument?.prewarmWaitMs ?? 0,
          document_total: documentTiming?.totalMs ?? 0,
          canonical_pdf: documentTiming?.canonicalPdfMs ?? 0,
          pdfjs_flatten_render: documentTiming?.pdfjsFlattenRenderMs ?? 0,
          flattened_pdf_encode: documentTiming?.flattenedPdfEncodeMs ?? 0,
          webp_encode: documentTiming?.webpEncodeMs ?? 0,
          ...(persistenceTimings.server_number_lookup !== undefined ? { server_number_lookup: persistenceTimings.server_number_lookup } : {}),
          ...(persistenceTimings.indexeddb_write !== undefined ? { indexeddb_write: persistenceTimings.indexeddb_write } : {}),
          ...(persistenceTimings.recovery_snapshot !== undefined ? { recovery_snapshot: persistenceTimings.recovery_snapshot } : {}),
        },
        blob_bytes: completionBlobs,
        ...(error ? { error } : {}),
      });
      completionTelemetryWritten = true;
    };
    try {
      const payload = {
        capture_mode: manualWithdrawal ? "manual" as const : "automatic" as const,
        withdrawal_date: withdrawalDate || new Date().toISOString().slice(0, 10),
        material_number: labelData.materialNumber.trim(),
        material_text: labelData.materialText.trim(),
        material_type: labelData.materialType.trim() || null,
        building: location.building,
        floor: location.floor,
        cabinet: location.cabinet,
        compartment: location.compartment,
        sub_compartment: location.subCompartment,
        quantity_removed: quantityRemoved.trim(),
        current_stock: currentStock.trim(),
        accounting_system: selectedAllocation.plant,
        accounting_subarea: selectedAllocation.plantArea,
        accounting_number: selectedAllocation.orderNumber,
        accounting_manufacturer: selectedAllocation.manufacturer || null,
        order_info: labelData.orderInfo.trim() || null,
        remark: null,
        persons: selectedPersons.map((name) => ({ name })),
        ...(editWithdrawalId !== null ? { change_description: "Entnahme bearbeitet." } : {}),
      };
      const simulationCaseUuid = voSimulation ? (voCaptureClientUuid ?? crypto.randomUUID()) : null;
      if (voSimulation && simulationCaseUuid && !voCaptureClientUuid) setVoCaptureClientUuid(simulationCaseUuid);
      const visionFeedbackBlob = manualWithdrawal ? null : buildVisionFeedbackBlob({
        commitment: "completed",
        baseline: visionFeedbackBaselineRef.current,
        finalLabelData: labelData,
        interventions: visionFeedbackInterventionsRef.current,
        step2ConfirmedAt: step2ConfirmedAtRef.current,
        retakes: visionRetakesRef.current,
        scanDetails,
        ocrWarpAvailable: Boolean(analysisFile),
        clientUuid: simulationCaseUuid ?? draftClientUuid ?? editClientUuid ?? null,
        withdrawalNumber: voSimulation ? null : (draftWithdrawalNumber ?? editWithdrawalNumber ?? null),
        serverId: voSimulation ? null : editWithdrawalId,
      });
      if (voSimulation) {
        if (getLastKnownAuthUser()?.role !== "admin") {
          throw new Error("VO-Testaufnahmen sind ausschließlich für Administratoren verfügbar.");
        }
        if (editWithdrawalId !== null || continuedWorkflow !== null || draftClientUuid !== null) {
          throw new Error("VO-Testaufnahme kann nicht aus einem Entwurf oder einer bestehenden Entnahme erzeugt werden.");
        }
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          throw new Error("VO-Testaufnahmen werden direkt im getrennten VO-Backend gespeichert. Dafür ist eine Serververbindung erforderlich.");
        }
        const caseUuid = simulationCaseUuid;
        if (!caseUuid) throw new Error("VO-Testaufnahme konnte keine stabile Capture-ID erzeugen.");
        const caseRef = `vo_capture:${caseUuid}`;
        const diagnostics = await createAdminDiagnostics(payload.material_number, payload.withdrawal_date, {
          withdrawalNumber: null,
          serverId: null,
          clientUuid: caseUuid,
          caseRef,
          captureOrigin: "vo_bootstrap",
        });
        const created = await createVisionCaptureCase({
          clientUuid: caseUuid,
          workflow: {
            withdrawal_date: payload.withdrawal_date,
            material_number: payload.material_number,
            material_text: payload.material_text,
            material_type: payload.material_type,
            storage_address: normalizeStorageAddress(labelData.storageAddress),
            quantity_removed: payload.quantity_removed,
            current_stock: payload.current_stock,
            accounting_system: payload.accounting_system,
            accounting_subarea: payload.accounting_subarea,
            accounting_number: payload.accounting_number,
            accounting_manufacturer: payload.accounting_manufacturer,
            order_info: payload.order_info,
            persons: selectedPersons,
            app_version: APP_VERSION,
          },
          artifacts: {
            label: controlFile,
            source: originalCaptureFile ?? sourceFile ?? controlFile,
            detectionSource: sourceFile ?? originalCaptureFile ?? controlFile,
            analysisInput: analysisInputFile,
            rectificationInput: rectificationInputFile,
            ocrWarp: analysisFile,
            visionFeedback: visionFeedbackBlob,
            liveTraining: liveTrainingFile,
            debugImage: diagnostics.image,
            debugData: diagnostics.data,
          },
        });
        setCompletedVoCapture(created);
        setNotice(null);
        window.dispatchEvent(new Event("materialentnahme:status-refresh"));
        return;
      }
      if (editWithdrawalId === null) {
        completionDocument = await createFreshCommitArtifacts();
        const { artifacts } = completionDocument;
        const localInput = {
          withdrawalData: {
            capture_mode: payload.capture_mode,
            withdrawal_date: payload.withdrawal_date, material_number: payload.material_number, material_text: payload.material_text, material_type: payload.material_type,
            building: payload.building, floor: payload.floor, cabinet: payload.cabinet, compartment: payload.compartment, sub_compartment: payload.sub_compartment,
            quantity_removed: payload.quantity_removed, current_stock: payload.current_stock, accounting_system: payload.accounting_system, accounting_subarea: payload.accounting_subarea, accounting_number: payload.accounting_number, accounting_manufacturer: payload.accounting_manufacturer,
            order_info: payload.order_info, remark: payload.remark, persons: payload.persons,
          },
          pdfBlob: artifacts.pdf, pdfPreviewBlob: artifacts.preview, labelBlob: manualWithdrawal ? null : (controlFile ?? null), sourceBlob: manualWithdrawal ? null : (originalCaptureFile ?? null), detectionSourceBlob: manualWithdrawal ? null : (sourceFile ?? null), analysisInputBlob: manualWithdrawal ? null : (analysisInputFile ?? null), rectificationInputBlob: manualWithdrawal ? null : (rectificationInputFile ?? null), ocrWarpBlob: manualWithdrawal ? null : (analysisFile ?? null), visionFeedbackBlob: manualWithdrawal ? null : visionFeedbackBlob, liveTrainingBlob: manualWithdrawal ? null : (liveTrainingFile ?? null),
          pdfFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}.pdf`,
          pdfPreviewFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Vorschau.webp`,
          labelFilename: controlFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Kontrollbild.${controlFile.name.split(".").pop() || "jpg"}` : undefined,
          sourceFilename: originalCaptureFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Originalaufnahme.${originalCaptureFile.name.split(".").pop() || "jpg"}` : undefined,
        detectionSourceFilename: sourceFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_DetectionQuelle.${sourceFile.name.split(".").pop() || "jpg"}` : undefined,
          analysisInputFilename: analysisInputFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_OpenCV_Analyse.png` : undefined,
          rectificationInputFilename: rectificationInputFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_RectificationInput.png` : undefined,
          ocrWarpFilename: analysisFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_OCR_Warp.${analysisFile.name.split(".").pop() || "jpg"}` : undefined,
          visionFeedbackFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_VisionFeedback.json`,
          liveTrainingFilename: liveTrainingFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_LiveTraining.json` : undefined,
          debugImageBlob: null, debugDataBlob: null,
          debugImageFilename: undefined, debugDataFilename: undefined,
        };
        completionBlobs = completionBlobBytes(localInput);
        let record;
        if (draftClientUuid) {
          const existing = await getLocalWithdrawal(draftClientUuid);
          // Wenn der Entwurf noch keine serverbestätigte Nummer besitzt, wird beim
          // finalen Abschluss online nochmals die aktuelle persönliche Nummer beim
          // Server abgefragt. Nach geleertem Browsercache bleibt die sichtbare #ID
          // dadurch konsistent mit dem Serverstand. Offline bleibt die vorhandene
          // lokale Nummer erhalten.
          let localNumber = existing?.server_withdrawal_number
            ?? existing?.local_withdrawal_number
            ?? draftWithdrawalNumber
            ?? null;
          if (localNumber === null) {
            if (typeof navigator !== "undefined" && navigator.onLine) {
              const numberLookupStarted = performance.now();
              localNumber = await nextLocalWithdrawalNumber(existing?.owner_user_id);
              persistenceTimings.server_number_lookup = Math.round(performance.now() - numberLookupStarted);
            } else {
              localNumber = 1;
            }
          }
          record = await createWithdrawalWithIdentity(localInput, draftClientUuid, localNumber, { autoSync: false, telemetry: persistenceTelemetry });
        } else {
          record = await createWithdrawal(localInput, { autoSync: false, telemetry: persistenceTelemetry });
        }
        writeCompletionTelemetry("ok", record);
        createdWithdrawalId = record.server_withdrawal_id;
        setCompletedClientUuid(record.client_uuid);
        setCompletionGraceActive(true);
        setCompletedId(record.server_withdrawal_id ?? -1);
        setCompletedWithdrawalNumber(record.server_withdrawal_number ?? record.local_withdrawal_number ?? null);
        setCompletionSynced(record.sync_status === "synced");
        setCompletionDocumentsComplete(record.sync_status === "synced");
        setNotice(null);
        window.dispatchEvent(new Event("materialentnahme:status-refresh"));
        if (manualWithdrawal) {
          if (typeof navigator !== "undefined" && navigator.onLine) void syncSingleWithdrawal(record).catch(() => undefined);
        } else {
          const diagnosticsPromise = createAdminDiagnostics(payload.material_number, payload.withdrawal_date, {
            withdrawalNumber: record.server_withdrawal_number ?? record.local_withdrawal_number ?? null,
            serverId: record.server_withdrawal_id ?? null,
            clientUuid: record.client_uuid,
          });
          void attachDiagnosticsAndStartSync(record.client_uuid, record, diagnosticsPromise);
        }
        void persistSelectedNewNames();
        return;
      }

      if (!editClientUuid || editBaseVersion === null || editWithdrawalNumber === null) {
        throw new Error("Bearbeitungsidentität ist unvollständig. Entnahme bitte erneut aus dem Verlauf öffnen.");
      }
      completionDocument = await createFreshCommitArtifacts();
      const { artifacts } = completionDocument;
      const localInput = {
        withdrawalData: {
          capture_mode: payload.capture_mode,
          withdrawal_date: payload.withdrawal_date, material_number: payload.material_number, material_text: payload.material_text, material_type: payload.material_type,
          building: payload.building, floor: payload.floor, cabinet: payload.cabinet, compartment: payload.compartment, sub_compartment: payload.sub_compartment,
          quantity_removed: payload.quantity_removed, current_stock: payload.current_stock, accounting_system: payload.accounting_system, accounting_subarea: payload.accounting_subarea, accounting_number: payload.accounting_number, accounting_manufacturer: payload.accounting_manufacturer,
          order_info: payload.order_info, remark: payload.remark, persons: payload.persons,
        },
        pdfBlob: artifacts.pdf, pdfPreviewBlob: artifacts.preview, labelBlob: manualWithdrawal ? null : (controlFile ?? null), sourceBlob: manualWithdrawal ? null : (originalCaptureFile ?? null), detectionSourceBlob: manualWithdrawal ? null : (sourceFile ?? null), analysisInputBlob: manualWithdrawal ? null : (analysisInputFile ?? null), rectificationInputBlob: manualWithdrawal ? null : (rectificationInputFile ?? null), ocrWarpBlob: manualWithdrawal ? null : (analysisFile ?? null), visionFeedbackBlob: manualWithdrawal ? null : visionFeedbackBlob, liveTrainingBlob: manualWithdrawal ? null : (liveTrainingFile ?? null),
        pdfFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}.pdf`,
        pdfPreviewFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Vorschau.webp`,
        labelFilename: controlFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Kontrollbild.${controlFile.name.split(".").pop() || "jpg"}` : undefined,
        sourceFilename: originalCaptureFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_Originalaufnahme.${originalCaptureFile.name.split(".").pop() || "jpg"}` : undefined,
        detectionSourceFilename: sourceFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_DetectionQuelle.${sourceFile.name.split(".").pop() || "jpg"}` : undefined,
        analysisInputFilename: analysisInputFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_OpenCV_Analyse.png` : undefined,
        rectificationInputFilename: rectificationInputFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_RectificationInput.png` : undefined,
        ocrWarpFilename: analysisFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_OCR_Warp.${analysisFile.name.split(".").pop() || "jpg"}` : undefined,
        visionFeedbackFilename: `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_VisionFeedback.json`,
        liveTrainingFilename: liveTrainingFile ? `${labelData.materialNumber.trim()}_${payload.withdrawal_date}_LiveTraining.json` : undefined,
        debugImageBlob: null, debugDataBlob: null,
        debugImageFilename: undefined, debugDataFilename: undefined,
      };
      completionBlobs = completionBlobBytes(localInput);
      const record = await queueWithdrawalEdit(localInput, {
        clientUuid: editClientUuid,
        withdrawalId: editWithdrawalId,
        withdrawalNumber: editWithdrawalNumber,
        baseVersion: editBaseVersion,
      }, { autoSync: false, telemetry: persistenceTelemetry });
      writeCompletionTelemetry("ok", record);
      createdWithdrawalId = editWithdrawalId;
      setCompletedClientUuid(record.client_uuid);
      setCompletionGraceActive(true);
      setCompletedId(editWithdrawalId);
      setCompletedWithdrawalNumber(editWithdrawalNumber);
      setCompletionSynced(record.sync_status === "synced");
      setCompletionDocumentsComplete(record.sync_status === "synced");
      setNotice(null);
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
      if (manualWithdrawal) {
        if (typeof navigator !== "undefined" && navigator.onLine) void syncSingleWithdrawal(record).catch(() => undefined);
      } else {
        const diagnosticsPromise = createAdminDiagnostics(payload.material_number, payload.withdrawal_date, {
          withdrawalNumber: record.server_withdrawal_number ?? record.local_withdrawal_number ?? editWithdrawalNumber,
          serverId: record.server_withdrawal_id ?? editWithdrawalId,
          clientUuid: record.client_uuid,
        });
        void attachDiagnosticsAndStartSync(record.client_uuid, record, diagnosticsPromise);
      }
      void persistSelectedNewNames();
    } catch (error: unknown) {
      const detail = error instanceof Error ? error.message : "Unbekannter Fehler.";
      writeCompletionTelemetry("error", undefined, detail);
      if (createdWithdrawalId !== null) {
        setCompletionDocumentsComplete(false);
        setNotice(`Entnahme wurde gespeichert, aber der Abschluss ist noch nicht vollständig: ${detail}`);
        reportIssue({ id: `withdrawal-files-${createdWithdrawalId}`, severity: "error", title: "Abschluss unvollständig", detail });
      } else {
        setNotice(error instanceof Error ? error.message : "Entnahme konnte nicht abgeschlossen werden.");
      }
    } finally {
      setFinalizing(false);
    }
  }

  function applyMasterValues(fields: MaterialMismatchField[]): void {
    const record = currentComparisonForDialog.record;
    if (!record) {
      return;
    }
    for (const field of fields) {
      visionFeedbackInterventionsRef.current[field] = "master_dialog";
    }

    setLabelData((current) => ({
      ...current,
      materialText: fields.includes("materialText") ? record.description : current.materialText,
      materialType: fields.includes("materialType") ? record.type : current.materialType,
      storageAddress: fields.includes("storageAddress") ? (record.storageLocations[0] ?? current.storageAddress) : current.storageAddress,
    }));

    setMismatchOpen(false);
  }


  async function handleSuggestionPageChange(nextPage: number): Promise<void> {
    const safePage = Math.max(0, nextPage);
    try {
      const result = await findMaterialSuggestions(
        labelData.materialNumber,
        labelData.materialText,
        labelData.materialType,
        labelData.storageAddress,
        fuzzyPageSize,
        safePage * fuzzyPageSize
      );
      setSuggestions(result.suggestions);
      setSuggestionTotal(result.total);
      const pageCount = Math.max(1, Math.ceil(result.total / fuzzyPageSize));
      setSuggestionPage(Math.min(safePage, pageCount - 1));
    } catch (unknownError) {
      const message = unknownError instanceof Error ? unknownError.message : "Materialvorschläge konnten nicht geladen werden.";
      setNotice(message);
    }
  }

  function applySuggestion(suggestion: MaterialMasterSuggestion): void {
    for (const field of ["materialNumber", "materialText", "materialType", "storageAddress"] as VisionFeedbackField[]) {
      visionFeedbackInterventionsRef.current[field] = "suggestion";
    }
    setLabelData((current) => ({
      ...current,
      materialNumber: suggestion.record.materialNumber,
      materialText: suggestion.record.description,
      materialType: suggestion.record.type,
      storageAddress: suggestion.record.storageLocations[0] ?? current.storageAddress,
    }));
    setSuggestionsOpen(false);
  }

  async function saveDraft(): Promise<boolean> {
    if (voSimulation) {
      setNotice("VO-Testaufnahmen werden bewusst nicht als betriebliche Entwürfe gespeichert.");
      return false;
    }
    setDraftSaving(true);
    registerUnknownMaterialIfCommitted();
    const saveStarted = performance.now();
    try {
      const clientUuid = draftClientUuid ?? crypto.randomUUID();
      if (!draftClientUuid) setDraftClientUuid(clientUuid);

      // Phase 1 ist bewusst extrem klein: den sichtbaren Formularstand atomar
      // einfrieren und mit allen bereits vorhandenen Aufnahme-/Vision-Artefakten
      // lokal sichern. PDF/WebP werden NICHT mehr zum Blocker des Save-Buttons.
      // Eine bereits laufende Prewarm-Promise darf weiterlaufen; ansonsten startet
      // die Dokumentgeneration erst nach dem erfolgreichen IndexedDB-Commit.
      const snapshot = currentDocumentSnapshot();
      const prepared = preparedDocumentRef.current;
      const preparedPromise = prepared?.signature === snapshot.signature ? prepared.promise : null;
      const location = parseStorageAddress(snapshot.data.storageAddress);
      const artifactDate = snapshot.data.withdrawalDate;
      const safeMaterial = snapshot.data.materialNumber || "Entwurf";
      const draftData = {
        client_uuid: clientUuid,
        capture_mode: manualWithdrawal ? "manual" as const : "automatic" as const,
        withdrawal_date: artifactDate,
        material_number: snapshot.data.materialNumber,
        material_text: snapshot.data.materialText,
        material_type: snapshot.data.materialType || null,
        building: location?.building ?? "",
        floor: location?.floor ?? "",
        cabinet: location?.cabinet ?? "",
        compartment: location?.compartment ?? "",
        sub_compartment: location?.subCompartment ?? "",
        quantity_removed: snapshot.data.quantityRemoved,
        current_stock: snapshot.data.currentStock,
        accounting_system: snapshot.allocation?.plant ?? null,
        accounting_subarea: snapshot.allocation?.plantArea ?? null,
        accounting_number: snapshot.data.accountingNumber || null,
        accounting_manufacturer: snapshot.allocation?.manufacturer || null,
        order_info: snapshot.data.orderInfo || null,
        persons: snapshot.data.persons.map((name) => ({ name })),
        remark: null,
      };

      const draftVisionFeedback = manualWithdrawal ? null : buildDraftVisionFeedbackBlob(
        visionFeedbackBaselineRef.current,
        visionRetakesRef.current,
      );
      const immediate = await saveDraftOfflineFirst(clientUuid, draftData, {
        labelBlob: manualWithdrawal ? null : (controlFile ?? sourceFile ?? null),
        labelFilename: (controlFile ?? sourceFile) ? `${safeMaterial}_${artifactDate}_Kontrollbild.${(controlFile ?? sourceFile)?.name.split(".").pop() || "jpg"}` : null,
        sourceBlob: manualWithdrawal ? null : (originalCaptureFile ?? null),
        sourceFilename: originalCaptureFile ? `${safeMaterial}_${artifactDate}_Originalaufnahme.${originalCaptureFile.name.split(".").pop() || "jpg"}` : null,
        detectionSourceBlob: manualWithdrawal ? null : (sourceFile ?? null),
        analysisInputBlob: manualWithdrawal ? null : (analysisInputFile ?? null),
        rectificationInputBlob: manualWithdrawal ? null : (rectificationInputFile ?? null),
        ocrWarpBlob: manualWithdrawal ? null : (analysisFile ?? null),
        visionFeedbackBlob: manualWithdrawal ? null : draftVisionFeedback,
        liveTrainingBlob: manualWithdrawal ? null : (liveTrainingFile ?? null),
        liveTrainingFilename: liveTrainingFile ? `${safeMaterial}_${artifactDate}_LiveTraining.json` : null,
        detectionSourceFilename: sourceFile ? `${safeMaterial}_${artifactDate}_DetectionQuelle.${sourceFile.name.split(".").pop() || "jpg"}` : null,
        analysisInputFilename: analysisInputFile ? `${safeMaterial}_${artifactDate}_OpenCV_Analyse.png` : null,
        rectificationInputFilename: rectificationInputFile ? `${safeMaterial}_${artifactDate}_RectificationInput.png` : null,
        ocrWarpFilename: analysisFile ? `${safeMaterial}_${artifactDate}_OCR_Warp.${analysisFile.name.split(".").pop() || "jpg"}` : null,
        visionFeedbackFilename: draftVisionFeedback ? `${safeMaterial}_${artifactDate}_VisionFeedback.json` : null,
      }, {
        sync: false,
        preferredWithdrawalNumber: draftWithdrawalNumber,
        documentsPending: true,
        deferBackgroundSync: true,
      });

      const savedSignature = snapshot.signature;
      const savedRevision = Math.max(0, immediate.record.content_revision ?? 0);
      lastSavedDraftSignatureRef.current = savedSignature;
      setLastSavedDraftSignature(savedSignature);
      const number = immediate.record.server_withdrawal_number ?? immediate.record.local_withdrawal_number;
      const localSaveMs = Math.round(performance.now() - saveStarted);
      console.info(`[Materialentnahme][Draft-Save] lokaler Snapshot ${localSaveMs} ms · Revision ${immediate.record.content_revision}`);
      setNotice(`Entwurf #${number ?? "–"} gespeichert · Dokument wird vorbereitet.`);
      setDraftSaving(false);

      // Phase 2: exakt derselbe eingefrorene Snapshot erzeugt PDF + WebP. Der
      // CAS-Guard in updateDraftDocumentArtifacts verhindert, dass ein inzwischen
      // erneut gespeicherter oder finalisierter Datensatz alte Dokumente erhält.
      void (async () => {
        try {
          const documentStarted = performance.now();
          const artifacts = await (preparedPromise ?? cachePreparedArtifacts(snapshot));
          const withDocuments = await updateDraftDocumentArtifacts(
            clientUuid,
            savedRevision,
            {
              pdfBlob: artifacts.pdf,
              pdfFilename: `${safeMaterial}_${artifactDate}.pdf`,
              pdfPreviewBlob: artifacts.preview,
              pdfPreviewFilename: `${safeMaterial}_${artifactDate}_Vorschau.webp`,
            },
          );
          if (!withDocuments || withDocuments.record_type !== "draft" || Math.max(0, withDocuments.content_revision ?? 0) !== savedRevision) return;
          console.info(`[Materialentnahme][Draft-Save] Dokumente ${Math.round(performance.now() - documentStarted)} ms · Revision ${withDocuments.content_revision}`);

          // Erst nach lokaler Dokumentpersistierung darf der Draft zum Server.
          if (typeof navigator !== "undefined" && navigator.onLine) {
            setNotice(`Entwurf #${number ?? "–"} gespeichert · Dokument vorbereitet · Synchronisierung läuft …`);
            const syncedRecord = await syncLocalDraft(withDocuments).catch(() => null);
            if (syncedRecord?.sync_status === "synced") {
              const syncedNumber = syncedRecord.server_withdrawal_number ?? syncedRecord.local_withdrawal_number;
              setNotice(`Entwurf #${syncedNumber ?? "–"} gespeichert · Synchronisiert.`);
            } else {
              setNotice(`Entwurf #${number ?? "–"} gespeichert · Lokal verfügbar.`);
            }
          } else {
            setNotice(`Entwurf #${number ?? "–"} gespeichert · Lokal verfügbar.`);
          }
          window.dispatchEvent(new Event("materialentnahme:status-refresh"));
        } catch (unknownError) {
          const message = unknownError instanceof Error ? unknownError.message : "Materialentnahmeschein konnte im Hintergrund nicht vorbereitet werden.";
          // Die fachlichen Entnahmedaten sind bereits sicher gespeichert. Vor dem
          // nächsten Sync rekonstruiert ensureRequiredDocuments() PDF/WebP erneut.
          reportIssue({ id: `draft-document-${clientUuid}`, severity: "warning", title: "Entwurf gespeichert", detail: `${message} Das Dokument wird beim nächsten Öffnen/Sync erneut erzeugt.` });
          window.dispatchEvent(new Event("materialentnahme:status-refresh"));
        }
      })();

      // Reine Admin-Diagnose bleibt Best-Effort. Sie darf einen inzwischen neu
      // gespeicherten/abgeschlossenen Datensatz niemals mit einem älteren Draft-
      // Snapshot überschreiben. Deshalb nur auf derselben Inhaltsrevision mergen.
      if (!manualWithdrawal) void (async () => {
        try {
          const diagnostics = await createAdminDiagnostics(safeMaterial, artifactDate, {
            withdrawalNumber: immediate.record.server_withdrawal_number ?? immediate.record.local_withdrawal_number ?? null,
            serverId: immediate.record.server_withdrawal_id ?? null,
            clientUuid: immediate.record.client_uuid,
          });
          const debugImage = diagnostics.image ?? controlFile ?? sourceFile ?? null;
          const debugData = diagnostics.data ?? new Blob([JSON.stringify({
            draft: true,
            captured_at: new Date().toISOString(),
            scan_details: scanDetails ?? null,
            last_capture_failure: scanDetails ? null : (() => {
              const flight = getLastScanFailureFlight();
              return flight?.status === "failure" ? flight : null;
            })(),
            last_capture_flight: getLastScanFailureFlight(),
            label_data: labelData,
            pipeline_e2e: {
              schema_version: 1,
              system_label_data: visionFeedbackBaselineRef.current?.labelData ?? null,
              user_final_label_data: labelData,
              baseline_captured_at: visionFeedbackBaselineRef.current?.capturedAt ?? null,
            },
          }, null, 2)], { type: "application/json" });
          const latest = await getLocalWithdrawal(clientUuid);
          if (!latest || latest.record_type !== "draft" || latest.content_revision !== immediate.record.content_revision) return;
          await updateDraftDiagnostics(clientUuid, {
            debugImageBlob: debugImage,
            debugImageFilename: debugImage ? (diagnostics.imageName ?? `${safeMaterial}_${artifactDate}_Debug.${debugImage.type.includes("png") ? "png" : "jpg"}`) : null,
            debugDataBlob: debugData,
            debugDataFilename: diagnostics.dataName ?? `${safeMaterial}_${artifactDate}_Diagnose.json`,
          }, immediate.record.content_revision);
          await persistSelectedNewNames();
          window.dispatchEvent(new Event("materialentnahme:status-refresh"));
        } catch (unknownError) {
          const message = unknownError instanceof Error ? unknownError.message : "Entwurfsdiagnose konnte im Hintergrund nicht vollständig verarbeitet werden.";
          reportIssue({ id: `draft-background-${clientUuid}`, severity: "warning", title: "Entwurf gespeichert", detail: message });
        }
      })();
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Entwurf konnte nicht gespeichert werden.";
      reportIssue({ id: "draft-error", severity: "error", title: "Entwurf nicht gespeichert", detail: message });
      setNotice(message);
      setDraftSaving(false);
      return false;
    }
  }

  function clearCompletionState(): void {
    setCompletedId(null);
    setCompletedWithdrawalNumber(null);
    setCompletionDocumentsComplete(null);
    setCompletionSynced(null);
    setCompletionEmailStatus(null);
    setCompletedClientUuid(null);
    setCompletionGraceActive(false);
    setCompletionEmailError(null);
  }

  function startNewWithdrawal(): void {
    clearCompletionState();
    setManualWithdrawal(false);
    setVoSimulation(false);
    setVoCaptureClientUuid(null);
    setCompletedVoCapture(null);
    reportedUnknownMaterialsRef.current.clear();
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    visionRetakesRef.current = [];
    step2ConfirmedAtRef.current = null;
    setWithdrawalDate(new Date().toISOString().slice(0, 10));
    setQuantityRemoved("");
    setCurrentStock("");
    setSelectedPersons([]);
    setPendingNewNames([]);
    setSelectedAllocation(null);
    setAllocationQuery("");
    setAllocationResults([]);
    setAllocationTotal(0);
    setAllocationPage(0);
    setDraftClientUuid(null);
    setDraftWithdrawalNumber(null);
    setEditWithdrawalId(null);
    setEditClientUuid(null);
    setEditBaseVersion(null);
    setEditWithdrawalNumber(null);
    lastSavedDraftSignatureRef.current = null;
    setLastSavedDraftSignature(null);
    continuedBaselineSignatureRef.current = null;
    setLabelData(createEmptyRecognizedLabelData());
    setMasterComparison(createEmptyMaterialMasterComparison());
    setRememberedMismatch(null);
    setSuggestions([]);
    setNotice(null);
    setStep2ValidationAttempted(false);
    setStep3ValidationAttempted(false);
    autoMismatchEligibleRef.current = true;
    setCameraOpen(true);
  }

  function goBackFromWorkflow(): void {
    autoMismatchEligibleRef.current = false;
    requestBrowserBack();
  }

  function resetWithdrawalState(): void {
    ocrGenerationRef.current += 1;
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    visionRetakesRef.current = [];
    step2ConfirmedAtRef.current = null;
    // OCR-Worker bewusst warm halten. Das erneute Laden von Tesseract + Sprachdaten
    // nach jeder Entnahme war auf mobilen Geräten einer der größten Zeitfaktoren.
    setActivity(null);
    setCameraOpen(false);
    setDetailsOpen(false);
    setMismatchOpen(false);
    setSuggestionsOpen(false);
    setStartSection(null);
    setStep(1);
    setWithdrawalDate("");
    setQuantityRemoved("");
    setCurrentStock("");
    setSelectedPersons([]);
    setPendingNewNames([]);
    setPersonDialogOpen(false);
    setAllocationDialogOpen(false);
    setAllocationQuery("");
    setAllocationResults([]);
    setAllocationTotal(0);
    setAllocationPage(0);
    setSelectedAllocation(null);
    setPreviewOpen(false);
    clearCompletionState();
    setCompletedVoCapture(null);
    setVoSimulation(false);
    setManualWithdrawal(false);
    setVoCaptureClientUuid(null);
    setDraftClientUuid(null);
    setDraftWithdrawalNumber(null);
    setEditWithdrawalId(null);
    setEditClientUuid(null);
    setEditBaseVersion(null);
    setEditWithdrawalNumber(null);
    lastSavedDraftSignatureRef.current = null;
    setLastSavedDraftSignature(null);
    continuedBaselineSignatureRef.current = null;
    setControlFile(null);
    setAnalysisFile(null);
    setSourceFile(null);
    setAnalysisInputFile(null);
    setRectificationInputFile(null);
    setLiveTrainingFile(null);
    setOriginalCaptureFile(null);
    setScanDetails(null);
    setContinuedWorkflow(null);
    setLabelData(createEmptyRecognizedLabelData());
    setMasterComparison(createEmptyMaterialMasterComparison());
    setRememberedMismatch(null);
    setSuggestions([]);
    setSuggestionTotal(0);
    setSuggestionPage(0);
    setOcrDetails(createEmptyOcrDetails());
    setNotice(null);
    setStep2ValidationAttempted(false);
    setStep3ValidationAttempted(false);
    autoMismatchEligibleRef.current = false;
  }

  function abortWithdrawalNow(): void {
    setAbortTransition(true);
    setAbortDialogOpen(false);
    resetWithdrawalState();

    // Erst die Startseite rendern lassen, danach den internen History-Stack
    // aufräumen. So entsteht beim X-Abbruch kein weißer Zwischenframe.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        returnToAppRoot();
        window.setTimeout(() => setAbortTransition(false), 240);
      });
    });
  }

  function requestAbortWithdrawal(): void {
    const signature = currentDraftSignature();
    const currentAlreadySavedAsDraft =
      !voSimulation
      && draftClientUuid !== null
      && lastSavedDraftSignatureRef.current === signature;

    if (continuedWorkflow !== null && (step === 2 || step === 3)) {
      const changed = continuedBaselineSignatureRef.current !== null
        && continuedBaselineSignatureRef.current !== signature;
      if (changed && !currentAlreadySavedAsDraft) {
        setAbortDialogOpen(true);
        return;
      }
      abortWithdrawalNow();
      return;
    }

    const hasData =
      step > 1
      || Boolean(labelData.materialNumber.trim())
      || Boolean(quantityRemoved.trim())
      || Boolean(currentStock.trim())
      || selectedPersons.length > 0
      || selectedAllocation !== null;

    if (hasData && !currentAlreadySavedAsDraft) {
      setAbortDialogOpen(true);
      return;
    }
    abortWithdrawalNow();
  }

  async function loadStoredControlImage(withdrawalId: number, clientUuid?: string): Promise<void> {
    // Eine lokal archivierte Aufnahme hat Vorrang und funktioniert auch ohne Backend.
    if (clientUuid) {
      const local = await getLocalWithdrawal(clientUuid).catch(() => null);
      if (local?.label_blob) {
        const file = new File(
          [local.label_blob],
          local.label_filename ?? `Kontrollbild_${withdrawalId}.jpg`,
          { type: local.label_blob.type || "image/jpeg" },
        );
        if (controlPreviewUrl) URL.revokeObjectURL(controlPreviewUrl);
        setControlFile(file);
        setControlPreviewUrl(URL.createObjectURL(file));
        return;
      }
    }

    try {
      const { data } = await fetchHistoryDetail(withdrawalId);
      const label = data.files
        .filter((file) => file.version === data.item.current_version)
        .find((file) => file.file_type === "label");
      if (!label) { setControlFile(null); setControlPreviewUrl(null); return; }
      const response = await fetchWithTimeout(`/api/withdrawals/${withdrawalId}/files/${label.id}/content`, { credentials: "include", cache: "no-store" }, 1800);
      if (!response.ok) throw new Error(`Kontrollbild konnte nicht geladen werden (${response.status}).`);
      const blob = await response.blob();
      const file = new File([blob], label.original_filename, { type: label.mime_type || blob.type || "image/jpeg" });
      if (controlPreviewUrl) URL.revokeObjectURL(controlPreviewUrl);
      setControlFile(file);
      setControlPreviewUrl(URL.createObjectURL(file));
      setScanDetails({
        capturedAt: data.current.created_at,
        captureSource: "image-capture",
        processingSteps: [],
        quality: null, mapping: null, detection: null,
        rectification: { used: false, width: null, height: null, error: null },
        debugLines: [], ocr: null, totalProcessingTimeMs: null,
      });
    } catch {
      // Das Kontrollbild ist optional. Eine Offline-Bearbeitung darf deshalb nicht blockieren
      // und erhält auch keinen störenden technischen Fehlerbanner.
      setControlFile(null);
      setControlPreviewUrl(null);
    }
  }

  async function restoreDraftVisionFeedback(clientUuid: string): Promise<void> {
    const local = await getLocalWithdrawal(clientUuid).catch(() => null);
    const restored = await readVisionFeedbackBaseline(local?.vision_feedback_blob).catch(() => null);
    if (local?.ocr_warp_blob) {
      setAnalysisFile(new File(
        [local.ocr_warp_blob],
        local.ocr_warp_filename ?? `OCR_Warp_${clientUuid}.jpg`,
        { type: local.ocr_warp_blob.type || "image/jpeg" },
      ));
    }
    if (!restored) return;
    visionFeedbackBaselineRef.current = restored.baseline;
    visionRetakesRef.current = restored.retakes;
    visionFeedbackInterventionsRef.current = {};
    step2ConfirmedAtRef.current = null;
  }

  function resumeDraft(version: WithdrawalVersionData, clientUuid: string, withdrawalId?: number, withdrawalNumber?: number | null): void {
    clearCompletionState();
    setManualWithdrawal(version.capture_mode === "manual");
    setContinuedWorkflow("draft");
    void restoreDraftVisionFeedback(clientUuid);
    if (withdrawalId) void loadStoredControlImage(withdrawalId, clientUuid);
    setWithdrawalDate(version.withdrawal_date);
    const storageAddress = version.building && version.floor && version.cabinet && version.compartment && version.sub_compartment
      ? `${version.building}${version.floor.toUpperCase()} ${version.cabinet} ${version.compartment} ${version.sub_compartment}`
      : "";
    continuedBaselineSignatureRef.current = continuedVersionSignature(version, storageAddress);
    setLabelData((current) => ({
      ...current,
      materialNumber: version.material_number,
      materialText: version.material_text,
      materialType: version.material_type ?? "",
      storageAddress,
      orderInfo: version.order_info ?? "",
    }));
    setQuantityRemoved(version.quantity_removed ?? "");
    setCurrentStock(version.current_stock ?? "");
    setSelectedPersons(version.persons.map((person) => person.name));
    setSelectedAllocation(
      version.accounting_number
        ? {
            id: 0,
            entryKey: `draft-${clientUuid}`,
            orderNumber: version.accounting_number,
            plant: version.accounting_system ?? "",
            plantArea: version.accounting_subarea ?? "",
            manufacturer: version.accounting_manufacturer ?? "",
            remark: "",
          }
        : null
    );
    setDraftClientUuid(clientUuid);
    setDraftWithdrawalNumber(withdrawalNumber ?? null);
    setEditWithdrawalId(null);
    setEditClientUuid(null);
    setEditBaseVersion(null);
    setEditWithdrawalNumber(null);
    lastSavedDraftSignatureRef.current = null;
    setLastSavedDraftSignature(null);
    setPendingNewNames([]);
    autoMismatchEligibleRef.current = false;
    setNotice(`${withdrawalNumber ? `#${withdrawalNumber} ` : ""}Entwurf geladen. Die Entnahme kann jetzt fortgesetzt werden.`);
    setStep2ValidationAttempted(true);
    setStartSection(null);
    setStep(2);
  }

  function resumeEdit(version: WithdrawalVersionData, withdrawalId: number, clientUuid: string, withdrawalNumber: number): void {
    clearCompletionState();
    setManualWithdrawal(version.capture_mode === "manual");
    setContinuedWorkflow("edit");
    visionFeedbackBaselineRef.current = null;
    visionFeedbackInterventionsRef.current = {};
    visionRetakesRef.current = [];
    step2ConfirmedAtRef.current = null;
    void loadStoredControlImage(withdrawalId, clientUuid);
    setWithdrawalDate(version.withdrawal_date);
    const storageAddress = version.building && version.floor && version.cabinet && version.compartment && version.sub_compartment
      ? `${version.building}${version.floor.toUpperCase()} ${version.cabinet} ${version.compartment} ${version.sub_compartment}`
      : "";
    continuedBaselineSignatureRef.current = continuedVersionSignature(version, storageAddress);
    setLabelData((current) => ({
      ...current,
      materialNumber: version.material_number,
      materialText: version.material_text,
      materialType: version.material_type ?? "",
      storageAddress,
      orderInfo: version.order_info ?? "",
    }));
    setQuantityRemoved(version.quantity_removed ?? "");
    setCurrentStock(version.current_stock ?? "");
    setSelectedPersons(version.persons.map((person) => person.name));
    setSelectedAllocation(version.accounting_number ? {
      id: 0,
      entryKey: `edit-${withdrawalId}`,
      orderNumber: version.accounting_number,
      plant: version.accounting_system ?? "",
      plantArea: version.accounting_subarea ?? "",
      manufacturer: version.accounting_manufacturer ?? "",
      remark: "",
    } : null);
    setDraftClientUuid(null);
    setDraftWithdrawalNumber(null);
    setEditWithdrawalId(withdrawalId);
    setEditClientUuid(clientUuid);
    setEditBaseVersion(version.version);
    setEditWithdrawalNumber(withdrawalNumber);
    lastSavedDraftSignatureRef.current = null;
    setLastSavedDraftSignature(null);
    setPendingNewNames([]);
    autoMismatchEligibleRef.current = false;
    setNotice("Entnahme zur Bearbeitung geladen. Beim Abschluss wird eine neue Version erstellt.");
    setStep2ValidationAttempted(true);
    setStartSection(null);
    setStep(2);
  }

  if (abortTransition) {
    return (
      <StartPage
        onStartWithdrawal={startNewWithdrawal}
        onOpenSection={openStartSection}
      />
    );
  }

  if (step === 1 && startSection === "history") return <LazyAreaBoundary section="history" label="Verlauf" onBack={closeStartSectionByHistory}><Suspense fallback={<LazyAreaFallback label="Verlauf wird geladen …" />}><HistoryPage initialSelectedId={historyTargetId} initialSelectedLocalUuid={historyTargetLocalUuid} onBack={() => { setHistoryTargetId(null); setHistoryTargetLocalUuid(null); closeStartSectionByHistory(); }} onResumeDraft={resumeDraft} onResumeEdit={resumeEdit} /></Suspense></LazyAreaBoundary>;
  if (step === 1 && startSection === "masterData") return <LazyAreaBoundary section="masterData" label="Materialstammliste" onBack={closeStartSectionByHistory}><Suspense fallback={<LazyAreaFallback label="Materialstammliste wird geladen …" />}><MaterialMasterPage onBack={closeStartSectionByHistory} /></Suspense></LazyAreaBoundary>;
  if (step === 1 && startSection === "allocations") return <LazyAreaBoundary section="allocations" label="Kontierungen" onBack={closeStartSectionByHistory}><Suspense fallback={<LazyAreaFallback label="Kontierungen werden geladen …" />}><AllocationMasterPage onBack={closeStartSectionByHistory} /></Suspense></LazyAreaBoundary>;
  if (step === 1 && startSection === "settings") return (
    <LazyAreaBoundary section="settings" label="Einstellungen" onBack={closeStartSectionByHistory}>
      <Suspense fallback={<LazyAreaFallback label="Einstellungen werden geladen …" />}>
        <SettingsPage
          onBack={closeStartSectionByHistory}
          onOpenAdmin={() => setStartSection("admin")}
          initialNotificationsOpen={openSystemNotificationsFromNotice}
          onInitialNotificationsHandled={() => setOpenSystemNotificationsFromNotice(false)}
        />
      </Suspense>
    </LazyAreaBoundary>
  );
  if (step === 1 && startSection === "admin") return (
    <LazyAreaBoundary section="admin" label="Administration" onBack={closeStartSectionByHistory}>
      <Suspense fallback={<LazyAreaFallback label="Administration wird geladen …" />}>
        <AdminPage
          onBack={() => { setOpenAdminFailureSimulation(false); closeStartSectionByHistory(); }}
          onOpenVisualLab={() => setStartSection("visualLab")}
          onOpenVisionOptimizer={() => setStartSection("visionOptimizer")}
          initialFailureSimulation={openAdminFailureSimulation}
        />
      </Suspense>
    </LazyAreaBoundary>
  );
  if (step === 1 && startSection === "visualLab") return (
    <LazyAreaBoundary section="visualLab" label="Visual Lab" onBack={closeStartSectionByHistory}>
      <Suspense fallback={<LazyAreaFallback label="Visual Lab wird geladen …" />}>
        <VisualLabPage onBack={closeStartSectionByHistory} />
      </Suspense>
    </LazyAreaBoundary>
  );
  if (step === 1 && startSection === "visionOptimizer") return (
    <LazyAreaBoundary section="visionOptimizer" label="Vision Optimizer" onBack={closeStartSectionByHistory}>
      <Suspense fallback={<LazyAreaFallback label="Vision Optimizer wird geladen …" />}>
        <VisionOptimizerPage onBack={closeStartSectionByHistory} initialTarget={visionOptimizerTarget ?? undefined} />
      </Suspense>
    </LazyAreaBoundary>
  );

  if (step === 1) {
    return <>
      <StartPage onStartWithdrawal={startNewWithdrawal} onOpenSection={openStartSection} />
      {cameraOpen && <Suspense fallback={null}><CameraCaptureDialog open onClose={requestAbortWithdrawal} onCapture={handleCapture} allowManualWithdrawal={continuedWorkflow === null && editWithdrawalId === null} onManualWithdrawal={startManualWithdrawal} allowVoSimulation={getLastKnownAuthUser()?.role === "admin" && continuedWorkflow === null && editWithdrawalId === null && voBootstrapPolicy?.available === true} voBootstrapProgress={voBootstrapPolicy ? { availableCaptures: voBootstrapPolicy.available_capture_cases, verified: voBootstrapPolicy.verified_ground_truth, target: voBootstrapPolicy.target_ground_truth, overrideEnabled: voBootstrapPolicy.override_enabled } : undefined} voSimulation={voSimulation} onVoSimulationChange={(enabled) => { setVoSimulation(enabled); setVoCaptureClientUuid(enabled ? (voCaptureClientUuid ?? crypto.randomUUID()) : null); }} /></Suspense>}
      <AppDialogSurface
        open={abortDialogOpen}
        onClose={() => setAbortDialogOpen(false)}
        title={voSimulation ? "VO-Testaufnahme abbrechen?" : "Materialentnahme abbrechen?"}
        subtitle={voSimulation ? "Die Testaufnahme wird verworfen. Es wurde keine betriebliche Entnahme angelegt." : "Nicht gespeicherte Eingaben gehen beim Verwerfen verloren."}
      >
        {!voSimulation && <Button
          variant="outlined"
          disabled={draftSaving || draftAlreadySaved}
          onClick={() => {
            void saveDraft().then((saved) => {
              if (saved) abortWithdrawalNow();
            });
          }}
          sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", color: APP_UI.accent, borderColor: APP_UI.accentBorder, fontWeight: 850 }}
        >
          {draftSaving ? "Entwurf wird gespeichert …" : "Als Entwurf speichern & beenden"}
        </Button>}
        <Button variant="contained" color="error" onClick={abortWithdrawalNow} sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
          Verwerfen & zur Startseite
        </Button>
        <Button onClick={() => setAbortDialogOpen(false)} sx={{ minHeight: 42, borderRadius: 999, color: "#c5c6ca", textTransform: "none", fontWeight: 800 }}>
          Weiter bearbeiten
        </Button>
      </AppDialogSurface>
    </>;
  }

  const heading = step === 2 ? (manualWithdrawal ? "Daten eingeben" : "Daten prüfen") : step === 3 ? (voSimulation ? "Testdaten erfassen" : "Entnahme erfassen") : (voSimulation ? "Testaufnahme prüfen" : "Entnahme prüfen");
  const materialNumberValid = /^983\d{7}$/.test(labelData.materialNumber.trim());
  const materialTextValid = labelData.materialText.trim().length > 0;
  const materialTypeValid = labelData.materialType.trim().length > 0;
  const storageAddressValid = isValidStorageAddress(labelData.storageAddress);
  const canContinueStep2 =
    materialNumberValid
    && materialTextValid
    && materialTypeValid
    && storageAddressValid;

  const fuzzyInputAvailable = Boolean(
    labelData.materialNumber.trim()
    || labelData.materialText.trim()
    || labelData.materialType.trim()
    || labelData.storageAddress.trim()
  );
  const exactMasterMatchAvailable =
    materialNumberValid
    && masterComparison.materialNumber === labelData.materialNumber.trim()
    && masterComparison.record !== null
    && (masterComparison.status === "match" || masterComparison.status === "mismatch");
  const currentComparisonForDialog =
    rememberedMismatch?.materialNumber === labelData.materialNumber.trim()
      ? rememberedMismatch
      : masterComparison;
  const canContinueStep3 = Boolean(quantityRemoved.trim() && currentStock.trim() && selectedPersons.length > 0 && selectedAllocation);
  const relevantServiceProblems = services.filter(
    (service) =>
      ["backendApi", "database", "fileStorage", "emailWorker", "smtp", "emailRecipient"].includes(service.id)
      && (service.state === "warning" || service.state === "error")
  );

  if (completedVoCapture !== null) {
    return (
      <Stack spacing={0.9} sx={{ maxWidth: 620, mx: "auto", minHeight: "calc(100dvh - 1rem)", justifyContent: "center", alignItems: "center", color: APP_UI.text, px: 1.25, py: 0.5, textAlign: "center" }}>
        <Box sx={{ width: 70, height: 70, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: "rgba(34,22,26,.98)", border: "1px solid rgba(199,0,48,.56)" }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 50, color: "#C70030" }} />
        </Box>
        <Typography variant="overline" sx={{ color: "#C70030", fontWeight: 950, letterSpacing: ".08em" }}>VO-TESTAUFNAHME GESPEICHERT</Typography>
        <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.08 }}>Realer VO-Lernfall bereit</Typography>
        <Typography sx={{ color: APP_UI.textMuted }}>{completedVoCapture.display_id} · {labelData.materialNumber} · {labelData.materialText}</Typography>
        <AppCard sx={{ width: "100%", p: 1, textAlign: "left", borderColor: "rgba(199,0,48,.52)", bgcolor: "rgba(34,22,26,.98)" }}>
          <Stack spacing={0.5}>
            <Typography sx={{ color: "#C70030", fontWeight: 850 }}>✓ Separat im VO-Backend gespeichert</Typography>
            <Typography sx={{ color: APP_UI.text, fontWeight: 800 }}>✓ Keine betriebliche Entnahme angelegt</Typography>
            <Typography sx={{ color: APP_UI.text, fontWeight: 800 }}>✓ Keine SID vergeben · kein normaler Verlauf · kein E-Mail-Versand</Typography>
            <Typography variant="caption" sx={{ color: APP_UI.textMuted }}>Ground Truth bleibt bewusst offen und wird später manuell im Vision Optimizer gesetzt.</Typography>
          </Stack>
        </AppCard>
        <Button fullWidth variant="contained" onClick={() => { resetWithdrawalState(); window.setTimeout(() => startNewWithdrawal(), 0); }} sx={{ borderRadius: 999, minHeight: 46, fontWeight: 900 }}>Nächste Aufnahme starten</Button>
        <Button fullWidth variant="outlined" onClick={() => { resetWithdrawalState(); setStartSection("visionOptimizer"); }} sx={{ borderRadius: 999, minHeight: 42 }}>Vision Optimizer öffnen</Button>
        <Button fullWidth onClick={resetWithdrawalState} sx={{ minHeight: 40, color: APP_UI.textMuted }}>Zur Startseite</Button>
      </Stack>
    );
  }

  if (completedId !== null) {
    const documentsOk = completionDocumentsComplete === true;
    return (
      <Stack spacing={0.8} sx={{ maxWidth: 620, mx: "auto", minHeight: "calc(100dvh - 1rem)", justifyContent: "center", alignItems: "center", color: "#fff", px: 1.25, py: 0.5, textAlign: "center" }}>
        <Box sx={{ width: 70, height: 70, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: "rgba(105,210,125,.10)", border: "1px solid rgba(105,210,125,.28)" }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 50, color: "#69d27d" }} />
        </Box>
        {editWithdrawalId !== null && <Typography variant="overline" sx={{ color: APP_UI.accent, fontWeight: 900 }}>ENTNAHME AKTUALISIERT</Typography>}
        <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.08 }}>{editWithdrawalId !== null ? "Änderungen gespeichert" : "Materialentnahme abgeschlossen"}</Typography>
        <Typography sx={{ color: APP_UI.textMuted }}>{completedWithdrawalNumber !== null ? `#${completedWithdrawalNumber} · ` : ""}{labelData.materialNumber} · {labelData.materialText}</Typography>

        <AppCard sx={{ width: "100%", p: 1, minHeight: 126, textAlign: "left", borderColor: documentsOk ? "rgba(105,210,125,.30)" : "rgba(244,163,64,.32)" }}>
          <Stack spacing={.45}>
            <Typography noWrap sx={{ minHeight: 22, color: "#69d27d", fontWeight: 850 }}>✓ Lokal sicher gespeichert</Typography>
            <Typography noWrap sx={{ minHeight: 22, color: completionSynced ? "#69d27d" : APP_UI.warning, fontWeight: 800 }}>
              {completionSynced ? "✓ Mit Server synchronisiert" : "⏳ Synchronisierung läuft …"}
            </Typography>
            <Typography
              noWrap
              sx={{
                minHeight: 22,
                color: !completionSynced ? APP_UI.textMuted : completionEmailStatus === "sent" ? APP_UI.success : completionEmailStatus === "failed" ? APP_UI.error : APP_UI.warning,
                fontWeight: 800,
              }}
            >
              {!completionSynced
                ? "⏳ E-Mail folgt nach Synchronisierung"
                : completionEmailStatus === "sent"
                  ? "✓ E-Mail erfolgreich versendet"
                  : completionEmailStatus === "failed"
                    ? "✕ E-Mail-Versand fehlgeschlagen"
                    : completionEmailStatus === "processing"
                      ? "⏳ E-Mail wird versendet"
                      : completionEmailStatus === "retry_wait"
                        ? "⏳ E-Mail-Versand wird erneut versucht"
                        : "⏳ E-Mail-Versand ausstehend"}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ minHeight: 18, color: completionEmailStatus === "failed" ? APP_UI.error : APP_UI.textMuted, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {completionEmailStatus === "failed" && completionEmailError
                ? completionEmailError
                : completionSynced
                  ? "Server-Sync abgeschlossen · Versandstatus wird automatisch aktualisiert."
                  : completionGraceActive
                    ? "Lokaler Abschluss sicher · Serverstatus wird geprüft."
                    : "Automatische Synchronisierung läuft im Hintergrund weiter."}
            </Typography>
          </Stack>
        </AppCard>

        {notice && <Alert severity="warning" sx={{ width: "100%", textAlign: "left", borderRadius: 2.7 }}>{notice}</Alert>}

        <Button fullWidth variant="contained" onClick={() => { resetWithdrawalState(); window.setTimeout(() => startNewWithdrawal(), 0); }} sx={{ borderRadius: 999, minHeight: 46, fontWeight: 900 }}>Neue Materialentnahme starten</Button>
        <Button fullWidth variant="outlined" onClick={() => { setCompletedId(null); setCompletedWithdrawalNumber(null); setCompletedClientUuid(null); setCompletionGraceActive(false); setCompletionDocumentsComplete(null); setCompletionSynced(null); setCompletionEmailStatus(null); setCompletionEmailError(null); setEditWithdrawalId(null); setStep(1); setStartSection("history"); }} sx={{ borderRadius: 999, minHeight: 42 }}>Im Verlauf anzeigen</Button>
        <Button fullWidth onClick={resetWithdrawalState} sx={{ minHeight: 40, color: APP_UI.textMuted }}>Zur Startseite</Button>
      </Stack>
    );
  }

  return <>
    <Stack spacing={1.15} sx={{ width: "100%", maxWidth: 620, color: "#f5f5f7", mx: "auto", pb: "calc(82px + env(safe-area-inset-bottom))" }}>
      <WorkflowPageHeader
        step={step}
        totalSteps={voSimulation ? 2 : 4}
        title={heading}
        onBack={goBackFromWorkflow}
        showBack={step === 4}
        actions={
          <Stack direction="row" spacing={0.55}>
            {step === 2 && exactMasterMatchAvailable && (
              <IconButton
                onClick={() => setMismatchOpen(true)}
                aria-label="Materialstamm-Abgleich öffnen"
                title="Materialstamm-Abgleich"
                sx={{
                  width: 40,
                  height: 40,
                  border: `1px solid ${APP_UI.warning}`,
                  color: APP_UI.warning,
                }}
              >
                <CompareArrowsRoundedIcon fontSize="small" />
              </IconButton>
            )}
            {step === 2 && !exactMasterMatchAvailable && fuzzyInputAvailable && (
              <IconButton
                onClick={() => setSuggestionsOpen(true)}
                aria-label="Materialvorschläge öffnen"
                title="Materialvorschläge"
                sx={{
                  width: 40,
                  height: 40,
                  border: `1px solid ${APP_UI.accentBorder}`,
                  color: APP_UI.accent,
                }}
              >
                <SearchRoundedIcon fontSize="small" />
              </IconButton>
            )}
            {step === 2 && (
              <IconButton
                onClick={() => setDetailsOpen(true)}
                aria-label="Scan-Details öffnen"
                disabled={!detailsAvailable}
                sx={{ width: 40, height: 40, border: `1px solid ${APP_UI.accentBorder}`, color: APP_UI.accent }}
              >
                <FactCheckOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={requestAbortWithdrawal}
              aria-label="Materialentnahme abbrechen"
              title="Materialentnahme abbrechen"
              sx={{
                width: 40,
                height: 40,
                color: "#ff6b70",
                border: "1px solid rgba(255,91,98,.62)",
                bgcolor: "rgba(255,91,98,.055)",
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      />

      {voSimulation && (
        <AppCard sx={{ p: 0.9, borderColor: "rgba(199,0,48,.56)", bgcolor: "rgba(34,22,26,.98)" }}>
          <Typography variant="overline" sx={{ color: "#C70030", fontWeight: 950, letterSpacing: ".08em" }}>VO-TESTAUFNAHME</Typography>
          <Typography variant="body2" sx={{ color: APP_UI.text, fontWeight: 850 }}>Keine echte Entnahme · keine SID · kein normaler Verlauf · kein Versand</Typography>
          <Typography variant="caption" sx={{ color: APP_UI.textMuted }}>Der Fall wird ausschließlich als reale VO-Lernaufnahme gespeichert. Ground Truth setzt du später manuell im Vision Optimizer.</Typography>
        </AppCard>
      )}

      {step === 2 && <>
        {scanDetails && (!scanDetails.detection?.found || !scanDetails.detection.finalGeometryAccepted || !scanDetails.rectification.used) && <Alert severity="warning" sx={{ borderRadius: 2.7 }}>Kein gültiges Etikett erkannt. Bitte Angaben manuell prüfen oder neu aufnehmen.</Alert>}
        {ocrDetails.status === "error" && <Alert severity="warning" sx={{ borderRadius: 2.7 }}>OCR konnte nicht abgeschlossen werden. Die Felder können manuell ausgefüllt werden.</Alert>}
        {masterComparison.status === "not-found" && <Alert severity="warning" sx={{ borderRadius: 2.7 }}>Materialnummer nicht in der Materialstammliste gefunden.</Alert>}
        {masterComparison.status === "error" && <Alert severity="error" sx={{ borderRadius: 2.7 }}>{masterComparison.message}</Alert>}
        <Stack spacing={1.05}>
          <AppTextField
            label="Materialnummer"
            value={labelData.materialNumber}
            onChange={(value) => updateLabelData("materialNumber", value)}
            inputMode="numeric"
            error={step2ValidationAttempted && !materialNumberValid}
            helperText={step2ValidationAttempted && !materialNumberValid ? "Materialnummer muss aus 10 Ziffern bestehen und mit 983 beginnen." : undefined}
          />
          <AppTextField
            label="Bezeichnung"
            value={labelData.materialText}
            onChange={(value) => updateLabelData("materialText", value)}
            error={step2ValidationAttempted && !materialTextValid}
            helperText={step2ValidationAttempted && !materialTextValid ? "Bezeichnung darf nicht leer sein." : undefined}
          />
          <AppTextField
            label="Typ"
            value={labelData.materialType}
            onChange={(value) => updateLabelData("materialType", value)}
            error={step2ValidationAttempted && !materialTypeValid}
            helperText={
              step2ValidationAttempted && !materialTypeValid
                ? "Typ darf nicht leer sein."
                : undefined
            }
          />
          <AppTextField
            label="Lagerort (Gebäude Lagerort Feld Fach)"
            value={labelData.storageAddress}
            onChange={(value) => updateLabelData("storageAddress", value)}
            autoComplete="off"
            error={step2ValidationAttempted && !storageAddressValid}
            helperText={step2ValidationAttempted && !storageAddressValid ? "Lagerort im Format 03E 153 03 01 eingeben." : undefined}
          />
        </Stack>
      </>}

      {step === 3 && <Stack spacing={1.15}>
        <AppTextField label="Entnommene Menge" value={quantityRemoved} onChange={setQuantityRemoved} inputMode="numeric" error={step3ValidationAttempted && !quantityRemoved.trim()} helperText={step3ValidationAttempted && !quantityRemoved.trim() ? "Entnommene Menge ist ein Pflichtfeld." : undefined} />
        <AppTextField label="Aktueller Lagerbestand" value={currentStock} onChange={setCurrentStock} inputMode="numeric" error={step3ValidationAttempted && !currentStock.trim()} helperText={step3ValidationAttempted && !currentStock.trim() ? "Aktueller Lagerbestand ist ein Pflichtfeld." : undefined} />
        <AppSelectionField
          label="Entnommen durch"
          placeholder="Person auswählen …"
          required
          chips={selectedPersons}
          error={step3ValidationAttempted && selectedPersons.length === 0}
          helperText={step3ValidationAttempted && selectedPersons.length === 0 ? "Mindestens eine Person auswählen." : undefined}
          onDeleteChip={(name) => setSelectedPersons((current) => current.filter((item) => item !== name))}
          onClick={() => setPersonDialogOpen(true)}
        />
        <AppSelectionField
          label="Kontierung"
          placeholder="Kontierung auswählen …"
          required
          value={selectedAllocation ? `${formatAllocationNumber(selectedAllocation.orderNumber)} · ${selectedAllocation.plant}${selectedAllocation.manufacturer ? ` · ${selectedAllocation.manufacturer}` : ""}` : undefined}
          error={step3ValidationAttempted && !selectedAllocation}
          helperText={step3ValidationAttempted && !selectedAllocation ? "Eine Kontierung auswählen." : undefined}
          onClick={() => { setAllocationDialogOpen(true); void searchAllocations("", 0); }}
        />
        <AppTextField label="Bestellinfo (optional)" value={labelData.orderInfo} onChange={(value) => updateLabelData("orderInfo", value)} />
      </Stack>}

      {step === 4 && <Stack spacing={0.55}>
        <ReviewCard title="Material" lines={[`${labelData.materialNumber} · ${labelData.materialText}`, `${labelData.materialType || "–"} · ${labelData.storageAddress}`]} onEdit={() => setStep(2)} />
        <ReviewCard title="Entnahme" lines={[`Menge ${quantityRemoved} · Lagerbestand ${currentStock}`, selectedPersons.join(", ")]} onEdit={() => setStep(3)} />
        <ReviewCard title="Kontierung" lines={selectedAllocation ? [`${formatAllocationNumber(selectedAllocation.orderNumber)} · ${selectedAllocation.plant}`, selectedAllocation.plantArea, ...(selectedAllocation.manufacturer ? [`Hersteller: ${selectedAllocation.manufacturer}`] : [])] : ["–"]} onEdit={() => setStep(3)} />
        {labelData.orderInfo.trim() && (
          <Typography variant="caption" sx={{ color: APP_UI.textMuted, px: 0.4 }}>
            Bestellinfo: <b>{labelData.orderInfo.trim()}</b>
          </Typography>
        )}
        {relevantServiceProblems.length > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2.7, py: 0.35 }}>
            <b>Relevanter Dienst eingeschränkt:</b>{" "}
            {relevantServiceProblems.map((service) => service.id === "backendApi" ? "Server" : service.label).join(", ")}
          </Alert>
        )}
        <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => { void openDocumentPreview(); }} sx={{ borderRadius: 999, minHeight: 42, fontWeight: 850 }}>Vorschau</Button>
      </Stack>}

      {notice && <Alert severity="info" onClose={() => setNotice(null)} sx={{ borderRadius: 2.7 }}>{notice}</Alert>}
      {editWithdrawalId === null && !voSimulation && <Button variant="text" disabled={draftSaving || draftAlreadySaved} onClick={() => void saveDraft()} sx={{ alignSelf: "center", color: "#83cee0", fontWeight: 800 }}>{draftSaving ? "Entwurf wird gespeichert …" : draftAlreadySaved ? "Entwurf gespeichert" : "Als Entwurf speichern"}</Button>}
    </Stack>

    <Paper square elevation={8} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 20, px: 1.5, py: 1.15, pb: "calc(1.15rem + env(safe-area-inset-bottom))", backgroundColor: "rgba(18,18,18,.98)", borderTop: "1px solid rgba(255,255,255,.10)" }}>
      <Stack direction="row" spacing={1} sx={{ maxWidth: 620, mx: "auto" }}>
        {step === 2 && continuedWorkflow === null && <Button variant="outlined" startIcon={<ReplayOutlinedIcon />} onClick={handleRetake} sx={{ minHeight: 50, borderRadius: 999, borderColor: "#d41616", color: "#e32222", fontWeight: 800 }}>Neu</Button>}
        {step > 2 && <Button variant="outlined" onClick={goBackFromWorkflow} sx={{ minHeight: 50, borderRadius: 999, px: 2, fontWeight: 800 }}>Zurück</Button>}
        {step === 2 && (
          <Button
            variant="contained"
            aria-disabled={!canContinueStep2}
            endIcon={voSimulation ? <CheckCircleRoundedIcon /> : <ChevronRightOutlinedIcon />}
            disabled={voSimulation && finalizing}
            onClick={() => { if (voSimulation) void saveVoCaptureAfterStep2(); else handleContinue(); }}
            sx={{
              minHeight: 50,
              flex: 1,
              borderRadius: 999,
              fontWeight: 850,
              ...(!canContinueStep2 ? {
                bgcolor: "rgba(131,206,224,.10)",
                color: "rgba(210,236,242,.34)",
                boxShadow: "none",
                "&:hover": { bgcolor: "rgba(28,33,36,.90)", boxShadow: "none" },
              } : {}),
            }}
          >
            {voSimulation ? (finalizing ? "VO-Testaufnahme wird gespeichert …" : "VO-Testaufnahme speichern") : "Weiter zur Entnahme"}
          </Button>
        )}
        {step === 3 && <Button variant="contained" aria-disabled={!canContinueStep3} endIcon={<ChevronRightOutlinedIcon />} onClick={() => { if (!canContinueStep3) { setStep3ValidationAttempted(true); return; } setStep3ValidationAttempted(false); prepareStep4Artifacts(); setStep(4); }} sx={{ minHeight: 50, flex: 1, borderRadius: 999, fontWeight: 850, ...(!canContinueStep3 ? { bgcolor: "rgba(24,27,31,.84)", color: "rgba(210,236,242,.42)", boxShadow: "none", "&:hover": { bgcolor: "rgba(28,33,36,.90)", boxShadow: "none" } } : {}) }}>Weiter zur Prüfung</Button>}
        {step === 4 && <Button variant="contained" disabled={finalizing} startIcon={<CheckCircleRoundedIcon />} onClick={() => void finalizeWithdrawal()} sx={{ minHeight: 50, flex: 1, borderRadius: 999, fontWeight: 900 }}>{finalizing ? "Wird abgeschlossen …" : voSimulation ? "VO-Testaufnahme abschließen" : "Entnahme abschließen"}</Button>}
      </Stack>
    </Paper>

    <AppDialogSurface
      open={abortDialogOpen}
      onClose={() => setAbortDialogOpen(false)}
      title={voSimulation ? "VO-Testaufnahme abbrechen?" : "Materialentnahme abbrechen?"}
      subtitle={voSimulation ? "Die Testaufnahme wird verworfen. Es wurde keine betriebliche Entnahme angelegt." : "Nicht gespeicherte Eingaben gehen beim Verwerfen verloren."}
    >
      {editWithdrawalId === null && !voSimulation && (
        <Button
          variant="outlined"
          disabled={draftSaving || draftAlreadySaved}
          onClick={() => {
            void saveDraft().then((saved) => {
              if (saved) abortWithdrawalNow();
            });
          }}
          sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", color: APP_UI.accent, borderColor: APP_UI.accentBorder, fontWeight: 850 }}
        >
          {draftSaving ? "Entwurf wird gespeichert …" : "Als Entwurf speichern & beenden"}
        </Button>
      )}
      <Button
        variant="contained"
        color="error"
        onClick={abortWithdrawalNow}
        sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
      >
        Verwerfen & zur Startseite
      </Button>
      <Button onClick={() => setAbortDialogOpen(false)} sx={{ minHeight: 42, borderRadius: 999, color: "#c5c6ca", textTransform: "none", fontWeight: 800 }}>
        Weiter bearbeiten
      </Button>
    </AppDialogSurface>

    <PersonSelectionSheet
      open={personDialogOpen}
      names={availableNames}
      selected={selectedPersons}
      onSelect={selectPerson}
      onCreate={createTemporaryPerson}
      onClose={() => setPersonDialogOpen(false)}
    />

    <AllocationSelectionSheet
      open={allocationDialogOpen}
      query={allocationQuery}
      results={allocationResults}
      total={allocationTotal}
      page={allocationPage}
      pageSize={allocationPageSize}
      onQueryChange={(value) => { void searchAllocations(value, 0); }}
      onPageChange={(nextPage) => { void searchAllocations(allocationQuery, nextPage); }}
      onSelect={(item) => {
        setSelectedAllocation(item);
        setAllocationDialogOpen(false);
      }}
      onClose={() => setAllocationDialogOpen(false)}
    />

    <AppFullscreenDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vorschau" subtitle="Materialentnahmeschein und Kontrollbild vor dem Abschluss prüfen">
      <DocumentComparisonViewport
        items={[
          {
            key: "withdrawal-document",
            label: "Materialentnahmeschein",
            src: documentPreviewLoading ? null : documentPreviewUrl,
            alt: "Ausgefüllter Materialentnahmeschein",
            imageBackground: "#fff",
            emptyText: documentPreviewLoading ? "Vorschau wird erzeugt …" : "Keine Dokumentvorschau verfügbar.",
          },
          ...(controlPreviewUrl ? [{
            key: "control-image",
            label: "Kontrollbild Etikett",
            src: controlPreviewUrl,
            alt: "Kontrollbild des Etiketts",
          }] : []),
        ]}
      />
    </AppFullscreenDialog>

    {cameraOpen && <Suspense fallback={null}><CameraCaptureDialog open onClose={requestAbortWithdrawal} onCapture={handleCapture} allowManualWithdrawal={continuedWorkflow === null && editWithdrawalId === null} onManualWithdrawal={startManualWithdrawal} allowVoSimulation={getLastKnownAuthUser()?.role === "admin" && continuedWorkflow === null && editWithdrawalId === null && voBootstrapPolicy?.available === true} voBootstrapProgress={voBootstrapPolicy ? { availableCaptures: voBootstrapPolicy.available_capture_cases, verified: voBootstrapPolicy.verified_ground_truth, target: voBootstrapPolicy.target_ground_truth, overrideEnabled: voBootstrapPolicy.override_enabled } : undefined} voSimulation={voSimulation} onVoSimulationChange={(enabled) => { setVoSimulation(enabled); setVoCaptureClientUuid(enabled ? (voCaptureClientUuid ?? crypto.randomUUID()) : null); }} /></Suspense>}
    <ScanDetailsDialog open={detailsOpen} details={scanDetails} controlFile={controlFile} controlUrl={controlPreviewUrl} analysisFile={analysisFile} sourceFile={sourceFile} adminMode={getLastKnownAuthUser()?.role === "admin"} continuedMode={continuedWorkflow !== null} onClose={() => setDetailsOpen(false)} />
    <MaterialMasterSuggestionsDialog
      open={suggestionsOpen}
      suggestions={suggestions}
      total={suggestionTotal}
      page={suggestionPage}
      pageSize={fuzzyPageSize}
      onPageChange={(page) => { void handleSuggestionPageChange(page); }}
      onClose={() => setSuggestionsOpen(false)}
      onSelect={applySuggestion}
    />
    <MaterialMasterMismatchDialog
      open={mismatchOpen && exactMasterMatchAvailable}
      materialNumber={currentComparisonForDialog.materialNumber}
      mismatches={currentComparisonForDialog.mismatches}
      masterDataCreatedAt={currentComparisonForDialog.masterDataCreatedAt}
      onClose={() => setMismatchOpen(false)}
      onApply={applyMasterValues}
    />
  </>;
}

function ReviewCard({ title, lines, onEdit }: { title: string; lines: string[]; onEdit: () => void }) {
  return (
    <AppCard sx={{ px: 1.15, py: 0.65 }}>
      <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: APP_UI.accent, fontWeight: 900 }}>{title}</Typography>
          {lines.map((line, index) => (
            <Typography
              key={`${title}-${index}`}
              variant="caption"
              noWrap
              sx={{
                display: "block",
                color: index === 0 ? "#fff" : "#a8a9ae",
                fontWeight: index === 0 ? 750 : 550,
                lineHeight: 1.35,
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>
        <Button onClick={onEdit} size="small" sx={{ minWidth: 0, px: 0.8, textTransform: "none" }}>
          Bearbeiten
        </Button>
      </Stack>
    </AppCard>
  );
}
